import React, { useEffect, useState, Suspense, useRef, useCallback } from "react";
import { isPublicClubPath } from "./utils/publicClubPaths";
import { rememberPostLoginDestination } from "./utils/postLoginDestination";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import { TenantProvider } from "./context/TenantContext";
import { PermissionProvider } from "./context/PermissionContext";
import { EntitlementProvider } from "./context/EntitlementContext";
import ModuleGate from "./components/common/ModuleGate";
import CallCenterProvider from "./components/calls/CallCenterProvider";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import { CartProvider } from "./context/CartContext";
import { toast } from "react-toastify";

// styles/main.css is imported once at the index.tsx entrypoint.

// Initialize i18n before React renders
import "./i18n";

// Public pages (eager — needed for fast FCP)
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import AcceptMembershipInvitePage from "./components/membership/AcceptMembershipInvitePage";
import Dashboard from "./pages/Dashboard";
// (Legacy /:clubid/athletes|programs|invoices|reports pages removed
// 2026-07-03 — they sat behind a bare PrivateRoute with no role or
// permission gate, so any authenticated member could open staff data
// views. Nothing linked to them; the real pages live under /manage/*.)

// Core components (eager)
import ScrollToTop from "./components/common/ScrollToTop";
import HelpAnchorScroller from "./help/helpAnchorNavigation";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import MagicLogin from "./pages/MagicLogin";
// Membership moved to React.lazy below — see "// ─── Lazy-loaded
// public pages ───" section. The eager import here drove FCP 13146ms
// on iOS 15 (Sentry CLUB-CENTRAL-TENANT-C).
import ProgramFinder from "./pages/ProgramFinder";
import MembershipSummary from "./pages/MembershipSummary";
import { useDispatch, useSelector } from "react-redux";
import {
  selectIsAdmin,
  selectIsMember,
  selectIsStaff,
  setIsAdmin,
  setIsMember,
  setIsStaff,
  setPermissions,
  setUser,
  setAuthzFromCapabilities,
} from "./store/slices/authSlice";
import SideNavbarLayout from "./components/layout/SideNavbarLayout";
import {
  getRolesWithPermissions,
} from "./api/permissionsAPI";
import { getCapabilities } from "./api/authAPI";
import { selectPermissionsForRole } from "./utils/roles";
import { setMembershipsList } from "./store/slices/membershipSlice";
import { getAllMemberships } from "./api/membershipAPI";
import { getAllAthletes } from "./api/athletesAPI";
import { setAllAthletes } from "./store/slices/athleteSlice";
// Storage access that cannot throw. Reading the window.localStorage PROPERTY
// raises a SecurityError in an iframe on a browser blocking third-party
// storage, and AppContent read it on its first line — so the whole app, the
// public /embed/* routes included, died before rendering anything. See
// @mediatechgroup/app-version's safe storage helpers.
import { safeGetItem, safeSetItem, safeRemoveItem } from "@mediatechgroup/app-version";
import { persistAuthToken } from "./api/authTokenStore";
import { getTenant } from "./api/usersAPI";
import { initializeTenantSupabase, supabase } from "./utils/supabaseClient";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import ClubProgramsPage from "./pages/ProgramsPage";
import FAQ from "./pages/FAQ";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import Eula from "./pages/Eula";
import Support from "./pages/Support";
import AccessibilityPolicy from "./pages/AccessibilityPolicy";
import { getSettings } from "./api/SettingsAPI";
import MemberInvoice from "./pages/MemberInvoice";
import { setSettings } from "./store/slices/settingsSlice";
import ProtectedRoute from "./components/common/ProtectedRoute";
import TenantSearchPage from "./pages/TenantSearchPage";
import TrialSignup from "./pages/TrialSignup";
import TrialConvert from "./pages/TrialConvert";
import { OfflineQueueProvider } from "./hooks/useOfflineQueue";
import { patchMembershipDetails } from "./api/membershipAPI";
import { patchAthlete } from "./api/athletesAPI";
import { NotificationProvider } from "./context/NotificationContext";
import { useAppVersion } from "./hooks/useAppVersion";
import UpdateAvailableBanner from "./components/common/UpdateAvailableBanner";
import { ChatProvider } from "./context/ChatContext";
import FloatingChatGate from "./components/common/FloatingChatGate";
import StaffIdleSignOut from "./components/common/StaffIdleSignOut";
import { KioskMode } from "./components/kiosk";
import { performLogout } from "./utils/logout";
import ErrorToastProvider from "./components/common/ErrorToastProvider";
import ComingSoonPage from "./components/common/ComingSoonPage";
import * as Sentry from "@sentry/react";
import { captureError, setTenantBranding } from "./utils/sentry";
import { applyCSSVariables, applyDocumentChrome } from "./utils/cssVariables";
import AnalyticsTracker from "./components/seo/AnalyticsTracker";
// Public pages (eager — shareable links, SEO)
import PublicEventPage from "./components/events/PublicEventPage";
import PublicEventsListPage from "./components/events/PublicEventsListPage";
import PublicCampsListPage from "./components/camps/PublicCampsListPage";
import PublicCampPage from "./components/camps/PublicCampPage";
import PublicClinicsListPage from "./components/clinics/PublicClinicsListPage";
import PublicClinicPage from "./components/clinics/PublicClinicPage";
import PublicFacilityPage from "./components/public/facility/PublicFacilityPage";
import PublicPostsPage from "./pages/PublicPostsPage";
import PublicBroadcastPage from "./components/broadcasts/PublicBroadcastPage";
import ViewPostPage from "./components/posts/ViewPostPage";
// Embed (lazy — external iframes; lazy-loading shrinks the main bundle and
// improves FCP for both the main app and the embed routes themselves, since
// each embed entry only pulls in the chunks it actually needs)
const EmbedRegistration = React.lazy(() => import("./components/embed/EmbedRegistration"));
const PublicFeedbackPage = React.lazy(() => import("./pages/PublicFeedbackPage"));
const SupportPinResetPage = React.lazy(() => import("./pages/SupportPinResetPage"));
const VolunteerCancelPage = React.lazy(() => import("./pages/VolunteerCancelPage"));
const ReviewFunnelPage = React.lazy(() => import("./components/marketing/ReviewFunnelPage"));
const SmsOptInPage = React.lazy(() => import("./pages/SmsOptInPage"));
const EmbedCamps = React.lazy(() => import("./components/embed/EmbedCamps"));
const EmbedCampDetail = React.lazy(() => import("./components/embed/EmbedCampDetail"));
const EmbedClinics = React.lazy(() => import("./components/embed/EmbedClinics"));
const EmbedClinicDetail = React.lazy(() => import("./components/embed/EmbedClinicDetail"));
const EmbedEvents = React.lazy(() => import("./components/embed/EmbedEvents"));
const EmbedEventDetail = React.lazy(() => import("./components/embed/EmbedEventDetail"));
const EmbedContact = React.lazy(() => import("./components/embed/EmbedContact"));

// /register — lazy-loaded. This route pulls in Stripe Elements
// (@stripe/react-stripe-js), the full registration form/wizard tree,
// BillingInfoForm, AthleteTable, etc. Eager import inflated the main
// bundle and was the largest contributor to FCP 13146ms on iOS 15
// Mobile Safari (Sentry CLUB-CENTRAL-TENANT-C). The Suspense fallback
// at the top of the Routes tree (line ~1459) renders a centered
// spinner while the chunk loads — well within the 13s budget we
// were busting before.
const Membership = React.lazy(() => import("./pages/Membership"));

// ─── Lazy-loaded admin/staff pages (code-split) ───
const StaffDashboard = React.lazy(() => import("./pages/StaffDashboard"));
const ConfigReviewPage = React.lazy(() => import("./pages/ConfigReviewPage"));
const RegisterMember = React.lazy(() => import("./components/staff/RegisterMember"));
const UpdateCardForm = React.lazy(() => import("./components/membership/UpdateCardForm"));
const ManageProfilePage = React.lazy(() => import("./pages/ManageProfilePage"));
const ManageAthletes = React.lazy(() => import("./components/athlete/ManageAthletes"));
const EditAthlete = React.lazy(() => import("./components/athlete/EditAthlete"));
const ManageMembershipsTable = React.lazy(() => import("./components/membership/table/ManageMembershipsTable"));
const ManageInvoiceTable = React.lazy(() => import("./components/invoice/staff"));
const InvoicePaymentHistory = React.lazy(() => import("./components/invoice/membership"));
const AttendancePage = React.lazy(() => import("./components/staff/attendance/AttendancePage"));
const RecentAbsencePage = React.lazy(() => import("./components/staff/attendance/RecentAbsencePage"));
const AttendanceNotesPage = React.lazy(() => import("./components/staff/attendance/AttendanceNotesPage"));
const StaffAthleteTable = React.lazy(() => import("./components/staff/athlete/StaffAthleteTable"));
const PendingReactivationsPage = React.lazy(() => import("./components/staff/athlete/PendingReactivationsPage"));
const ManageTeamsPage = React.lazy(() => import("./components/staff/teams/ManageTeamsPage"));
const ManageLevelsPage = React.lazy(() => import("./components/staff/levels/ManageLevelsPage"));
const StaffUpdateAthlete = React.lazy(() => import("./components/staff/athlete/StaffUpdateAthlete"));
const BroadcastWizard = React.lazy(() => import("./components/admin/email"));
const AddStaffPage = React.lazy(() => import("./components/admin/staff/add"));
const ManageSettingsPage = React.lazy(() => import("./components/admin/settings"));
const AutomationSettingsPage = React.lazy(() => import("./components/admin/AutomationSettingsPage"));
const InactivitySettingsPage = React.lazy(() => import("./components/admin/settings/InactivitySettings/InactivitySettingsPage"));
const StripeKillswitchPage = React.lazy(() => import("./components/admin/settings/StripeKillswitch/StripeKillswitchPage"));
const CustomDomainPage = React.lazy(() => import("./components/admin/settings/CustomDomain/CustomDomainPage"));
const CreateProgramPage = React.lazy(() => import("./components/admin/programs"));
const ProgramsPage = React.lazy(() => import("./components/admin/programs/ManageProgramPage"));
const ManageCategoriesPage = React.lazy(() => import("./components/admin/programs/ManageCategoriesPage"));
const RolePermissionsManager = React.lazy(() => import("./components/rolePermissions"));
const ProgramPricingSettings = React.lazy(() => import("./components/admin/settings/ProgramPricingSettings/ProgramPricingSettings"));
// InvoicePage (the antiquated "Invoice & Payment Management" report) was
// removed 2026-05-30. Reports > Revenue (invoiced/revenue toggle) and
// Reports > Accounts Receivable Aging cover the same operational ground
// without the legacy filter+drill-down maze. The component and its
// InvoiceAndPayment subdir were deleted; legacy /admin/invoices redirect
// now points at Reports > Revenue.
const ManageUnpaidInvoice = React.lazy(() => import("./components/invoice/staff/unpaid/ManageUnpaidInvoice"));
const NegativeBalances = React.lazy(() => import("./components/reports/NegativeBalances"));
const ReportsLandingPage = React.lazy(() => import("./components/reports/ReportsLandingPage"));
const SettingsLandingPage = React.lazy(() => import("./components/settings/SettingsLandingPage"));
const WhatsNewPage = React.lazy(() => import("./components/whatsnew/WhatsNewPage"));
const SubscriptionPage = React.lazy(() => import("./components/settings/SubscriptionPage"));
const ManageUsersPage = React.lazy(() => import("./components/admin/staff/manage"));
const ManageNotificationPage = React.lazy(() => import("./components/admin/settings/ManageNotifications/ManageNotificationPage"));
const EmailTemplatesPage = React.lazy(() => import("./components/admin/settings/ManageNotifications/EmailTemplatesPage"));
const FeeSettingsPage = React.lazy(() => import("./components/admin/settings/ManageSettings/FeeSettingsPage"));
const EmailSettingsPage = React.lazy(() => import("./components/admin/settings/ManageSettings/EmailSettingsPage"));
const EmailVerificationPage = React.lazy(() => import("./pages/EmailVerificationPage"));
const UnverifiedEmailsReportPage = React.lazy(() => import("./pages/UnverifiedEmailsReportPage"));
// URL-addressable membership editor. Wrapper that fetches by :membershipId
// and dispatches into Redux, then renders EditMembershipPage. Lets any
// /manage/memberships/{id} link work (previously fell through to *).
const MembershipDetailPage = React.lazy(() => import("./pages/MembershipDetailPage"));
// QB-equivalent financial reports that work under account-balance billing.
const PaymentMethodsBreakdownReport = React.lazy(() => import("./components/reports/PaymentMethodsBreakdownReport"));
const StripePayoutReconciliationReport = React.lazy(() => import("./components/reports/StripePayoutReconciliationReport"));
const YearEndStatementsReport = React.lazy(() => import("./components/reports/YearEndStatementsReport"));
const SharedMediaPage = React.lazy(() => import("./pages/SharedMediaPage"));
const StoreSettingsPage = React.lazy(() => import("./components/admin/settings/StoreSettings/StoreSettingsPage"));
const PosSettingsPage = React.lazy(() => import("./components/admin/settings/PosSettings/PosSettingsPage"));
const PosCheckoutPage = React.lazy(() => import("./components/store/PosCheckoutPage"));
const AdminOrdersPage = React.lazy(() => import("./components/admin/store/AdminOrdersPage"));
const AthleteLoginPage = React.lazy(() => import("./components/athlete/AthleteLoginPage"));
const AthleteProgressPage = React.lazy(() => import("./components/athlete/AthleteProgressPage"));
const SmsDiagnosticsPage = React.lazy(() => import("./components/admin/settings/SmsDiagnostics/SmsDiagnosticsPage"));
const GPSettingsPanel = React.lazy(() => import("./components/admin/settings/StoreSettings/GPSettingsPanel"));
const CommissionDashboard = React.lazy(() => import("./components/admin/settings/StoreSettings/CommissionDashboard"));
const PayInAdvance = React.lazy(() => import("./components/payment/advance/PayInAdvance"));
const MemberBillingHistory = React.lazy(() => import("./components/membership/BillingHistory/MemberBillingHistory"));
const PrivateContactPage = React.lazy(() => import("./pages/PrivateContactPage"));
const FeedbackPage = React.lazy(() => import("./pages/FeedbackPage"));
const News = React.lazy(() => import("./pages/News"));
const FamilyHelpPage = React.lazy(() => import("./components/member/help/FamilyHelpPage"));
const MembershipsListPage = React.lazy(() => import("./components/membership/MembershipsListPage"));
const GenerateManualInvoicePage = React.lazy(() => import("./components/staff/invoice/GenerateManualInvoicePage"));
const EditDraftInvoicePage = React.lazy(() => import("./components/staff/invoice/EditDraftInvoicePage"));
const CompetitionFeeBatchPage = React.lazy(() => import("./components/admin/billing/CompetitionFeeBatchPage"));
const AdminEnrollmentReport = React.lazy(() => import("./components/admin/AdminEnrollmentReport"));
const DataHealthPage = React.lazy(() => import("./components/admin/DataHealthPage"));
const FacilityManagePage = React.lazy(() => import("./components/admin/facility/FacilityManagePage"));
const MemberFacilityBookingPage = React.lazy(() => import("./components/member/facility/MemberFacilityBookingPage"));
const MemberFacilityBookingsPage = React.lazy(() => import("./components/member/facility/MemberFacilityBookingsPage"));
const ScheduledCancellationsPage = React.lazy(() => import("./components/admin/ScheduledCancellationsPage"));
const RevenueReport = React.lazy(() => import("./components/reports/RevenueReport"));
const ChurnRetentionReport = React.lazy(() => import("./components/reports/ChurnRetentionReport"));
const AgingReport = React.lazy(() => import("./components/reports/AgingReport"));
const IncidentReportsReport = React.lazy(() => import("./components/reports/IncidentReportsReport"));
const ProgramProfitabilityReport = React.lazy(() => import("./components/reports/ProgramProfitabilityReport"));
const AttendanceTrendsReport = React.lazy(() => import("./components/reports/AttendanceTrendsReport"));
const RegistrationFunnelReport = React.lazy(() => import("./components/reports/RegistrationFunnelReport"));
const CampEventFunnelReport = React.lazy(() => import("./components/reports/CampEventFunnelReport"));
const LeadsBySourceReport = React.lazy(() => import("./components/reports/LeadsBySourceReport"));
const StaffActivityReport = React.lazy(() => import("./components/reports/StaffActivityReport"));
const WebAnalyticsReport = React.lazy(() => import("./components/reports/WebAnalyticsReport"));
const NewMemberAcquisitionReport = React.lazy(() => import("./components/reports/NewMemberAcquisitionReport"));
const SeasonalComparisonReport = React.lazy(() => import("./components/reports/SeasonalComparisonReport"));
const TopCustomersBySpendReport = React.lazy(() => import("./components/reports/TopCustomersBySpendReport"));
// InvoiceQuantityTrendsReport was removed 2026-05-30 as antiquated.
// The "how many invoices per month broken down by type" view didn't
// carry operational value the Revenue and Aging reports don't already
// cover. Backend GET /reports/stats/invoice-quantity-trends + the
// component itself were both deleted.
const MembershipTenureReport = React.lazy(() => import("./components/reports/MembershipTenureReport"));
const MembershipDemographicsReport = React.lazy(() => import("./components/reports/MembershipDemographicsReport"));
const AthleteDemographicsReport = React.lazy(() => import("./components/reports/AthleteDemographicsReport"));
// EnrollmentNetInflowReport removed 2026-05-30 as Chris flagged it
// antiquated. The "weekly/monthly/quarterly inflow vs outflow vs net"
// view didn't carry operational value the Churn & Retention report
// doesn't already cover with its monthly enrolled/unenrolled/net line
// chart. Backend GET /reports/stats/enrollment-net-inflow + the
// component itself were both deleted.
// EnrollmentReasonsReport removed 2026-05-30 as antiquated. Backend
// GET /reports/stats/enrollment-reasons + the component itself were
// both deleted; sidebar entry under Reports > Operations was removed.
const ChurnRiskReport = React.lazy(() => import("./components/reports/ChurnRiskReport"));
const AnnualRegistrationFeeReport = React.lazy(() => import("./components/reports/AnnualRegistrationFeeReport"));
const BillingReconciliationReport = React.lazy(() => import("./components/reports/BillingReconciliationReport"));
const BillingRunReport = React.lazy(() => import("./components/reports/BillingRunReport"));
const RevenueProjectionsReport = React.lazy(() => import("./components/reports/RevenueProjectionsReport"));
const AthleteBirthdays = React.lazy(() => import("./components/admin/reporting/AthleteBirthdays"));
const ReassignAthletesPage = React.lazy(() => import("./pages/ReassignAthletesPage"));
const ManageCustomersTable = React.lazy(() => import("./components/customers/ManageCustomersTable"));
const EditCustomerPage = React.lazy(() => import("./components/customers/EditCustomerPage"));
const CreateManualInvoice = React.lazy(() => import("./components/staff/invoice/CreateManualInvoice"));
const AddAthlete = React.lazy(() => import("./components/athlete/AddAthlete"));
const SupportPage = React.lazy(() => import("./components/staff/SupportPage"));
const KnowledgebasePage = React.lazy(() => import("./components/staff/KnowledgebasePage"));
const MerchandisePage = React.lazy(() => import("./components/store/MerchandisePage"));
const EquipmentPage = React.lazy(() => import("./components/store/EquipmentPage"));
const MusicAdminPage = React.lazy(() => import("./components/store/MusicAdminPage"));
const StaffTrainingPage = React.lazy(() => import("./components/lms/StaffTrainingPage"));
const AthleteTrainingPage = React.lazy(() => import("./components/lms/AthleteTrainingPage"));
const LMSOverviewPage = React.lazy(() => import("./components/lms/LMSOverviewPage"));
const CourseDetailPage = React.lazy(() => import("./components/lms/CourseDetailPage"));
const CoursePlayerPage = React.lazy(() => import("./components/lms/CoursePlayerPage"));
const CourseBuilderPage = React.lazy(() => import("./components/lms/CourseBuilderPage"));
const ActivityLogPage = React.lazy(() => import("./components/activity/ActivityLogPage"));
const EnrollmentsPage = React.lazy(() => import("./components/lms/EnrollmentsPage"));
const AwardsPage = React.lazy(() => import("./components/lms/AwardsPage"));
const ManageCampsPage = React.lazy(() => import("./components/camps/ManageCampsPage"));
const CreateCampPage = React.lazy(() => import("./components/camps/CreateCampPage"));
const EditCampPage = React.lazy(() => import("./components/camps/EditCampPage"));
const CampAttendancePage = React.lazy(() => import("./components/camps/CampAttendancePage"));
const ManageClinicsPage = React.lazy(() => import("./components/clinics/ManageClinicsPage"));
const CreateClinicPage = React.lazy(() => import("./components/clinics/CreateClinicPage"));
const EditClinicPage = React.lazy(() => import("./components/clinics/EditClinicPage"));
const ClinicAttendancePage = React.lazy(() => import("./components/clinics/ClinicAttendancePage"));
const ManagePrivateSessionsPage = React.lazy(() => import("./components/privateSessions/ManagePrivateSessionsPage"));
const PrivateSessionSettingsSection = React.lazy(() => import("./components/privateSessions/PrivateSessionSettingsSection"));
const ManageEventsPage = React.lazy(() => import("./components/events/ManageEventsPage"));
const CreateEventPage = React.lazy(() => import("./components/events/CreateEventPage"));
const MyTasksPage = React.lazy(() => import("./components/tasks/MyTasksPage"));
const EventDetailPage = React.lazy(() => import("./components/events/EventDetailPage"));
const EditEventPage = React.lazy(() => import("./components/events/EditEventPage"));
const EventAttendancePage = React.lazy(() => import("./components/events/EventAttendancePage"));
const EventTemplatesPage = React.lazy(() => import("./components/events/EventTemplatesPage"));
const EventAnalyticsPage = React.lazy(() => import("./components/events/EventAnalyticsPage"));
const BirthdayPartiesPage = React.lazy(() => import("./components/events/BirthdayPartiesPage"));
const CompetitionSetupWizard = React.lazy(() => import("./components/admin/competitions/SetupWizard"));
const CompetitionDetailPage = React.lazy(() => import("./components/admin/competitions/CompetitionDetailPage"));
const PublicCompetitionPage = React.lazy(() => import("./pages/PublicCompetitionPage"));
const AwardsAnnouncementScreen = React.lazy(() => import("./components/admin/competitions/AwardsAnnouncementScreen"));
const EventRegistrationsPage = React.lazy(() => import("./components/events/EventRegistrationsPage"));
const EventSlotRosterPage = React.lazy(() => import("./components/events/EventSlotRosterPage"));
const LeadsPipelinePage = React.lazy(() => import("./components/crm").then(m => ({ default: m.LeadsPipelinePage })));
const LeadFunnelPage = React.lazy(() => import("./components/crm").then(m => ({ default: m.LeadFunnelPage })));
const ConvertedLeadsReviewPage = React.lazy(() => import("./components/crm").then(m => ({ default: m.ConvertedLeadsReviewPage })));
const AIReceptionistPage = React.lazy(() => import("./components/ai-config/AIReceptionistPage"));
const KnowledgeManager = React.lazy(() => import("./components/ai-config/KnowledgeManager"));
const AIConversationsPage = React.lazy(() => import("./components/ai-config/AIConversationsPage"));
const AIAnalyticsPage = React.lazy(() => import("./components/ai-config/AIAnalyticsPage"));
const CallRoutingSettingsPage = React.lazy(() => import("./components/ai-config/CallRoutingSettingsPage"));
const CallHistoryPage = React.lazy(() => import("./components/calls/CallHistoryPage"));
const AllPostsPage = React.lazy(() => import("./components/posts/AllPostsPage"));
const NewPostPage = React.lazy(() => import("./components/posts/NewPostPage"));
const EditPostPage = React.lazy(() => import("./components/posts/EditPostPage"));
const CategoriesPage = React.lazy(() => import("./components/posts/CategoriesPage"));
const AllBroadcastsPage = React.lazy(() => import("./components/marketing/AllBroadcastsPage"));
const BroadcastDetailPage = React.lazy(() => import("./components/marketing/BroadcastDetailPage"));
const ContactsPage = React.lazy(() => import("./components/marketing/ContactsPage"));
const SocialPage = React.lazy(() => import("./components/marketing/SocialPage"));
const LocalSeoPage = React.lazy(() => import("./components/marketing/LocalSeoPage"));
const AdvertisingIntelligencePage = React.lazy(() => import("./components/marketing/AdvertisingIntelligencePage"));
const ReputationAiPage = React.lazy(() => import("./components/marketing/ReputationAiPage"));
const MemberStorePage = React.lazy(() => import("./components/store/MemberStorePage"));
const CartPage = React.lazy(() => import("./components/store/CartPage"));
const CheckoutPage = React.lazy(() => import("./components/store/CheckoutPage"));
const MusicStorePage = React.lazy(() => import("./components/store/MusicStorePage"));
const OrderHistoryPage = React.lazy(() => import("./components/store/OrderHistoryPage"));
const OrderConfirmationPage = React.lazy(() => import("./components/store/OrderConfirmationPage"));
const StorefrontRouteGuard = React.lazy(() => import("./components/store/StorefrontRouteGuard"));
const MemberEventsPage = React.lazy(() => import("./components/events/MemberEventsPage"));
const CalendarPage = React.lazy(() => import("./components/events/CalendarPage"));
const MyEventsPage = React.lazy(() => import("./components/events/MyEventsPage"));
const MemberCampsPage = React.lazy(() => import("./components/camps/MemberCampsPage"));
const MemberMyCampsPage = React.lazy(() => import("./components/camps/MemberMyCampsPage"));
const CampCalendarPage = React.lazy(() => import("./components/camps/CampCalendarPage"));
const MemberClinicsPage = React.lazy(() => import("./components/clinics/MemberClinicsPage"));
const MemberMyClinicsPage = React.lazy(() => import("./components/clinics/MemberMyClinicsPage"));
const ClinicCalendarPage = React.lazy(() => import("./components/clinics/ClinicCalendarPage"));
const WaiversPage = React.lazy(() => import("./components/admin/settings/Waivers/WaiversPage"));
const WaiverBuilderPage = React.lazy(() => import("./components/admin/settings/Waivers/WaiverBuilderPage"));
const WaiverSendPage = React.lazy(() => import("./components/admin/settings/Waivers/WaiverSendPage"));
const WaiversMissingPage = React.lazy(() => import("./components/admin/settings/Waivers/WaiversMissingPage"));
const MedicalInfoMissingPage = React.lazy(() => import("./components/admin/settings/Waivers/MedicalInfoMissingPage"));
const SignWaiverPage = React.lazy(() => import("./pages/SignWaiverPage"));
const ActionTypeSettingsPage = React.lazy(() => import("./components/admin/settings/ActionTypeSettings/ActionTypeSettingsPage"));
const SupportSettingsPage = React.lazy(() => import("./components/admin/settings/SupportSettings/SupportSettingsPage"));
const KioskSettingsPage = React.lazy(() => import("./components/admin/settings/KioskSettings/KioskSettingsPage"));
const MarketingDashboard = React.lazy(() => import("./components/reports/MarketingDashboard"));
const DiscountCodesPage = React.lazy(() => import("./components/admin/marketing/DiscountCodesPage"));
const CampaignsPage = React.lazy(() => import("./components/admin/marketing/CampaignsPage"));
const MarketingCampaignsPage = React.lazy(() => import("./components/marketing/MarketingCampaignsPage"));
const DesignLabPage = React.lazy(() => import("./pages/DesignLabPage"));
const DesignEditorPage = React.lazy(() => import("./pages/DesignEditorPage"));
const PublicDesignViewerPage = React.lazy(() => import("./pages/PublicDesignViewerPage"));
const AIInsightsPage = React.lazy(() => import("./components/reports/AIInsightsPage"));
// HR Module
const ManageEmployeesPage = React.lazy(() => import("./components/hr/employees/ManageEmployeesPage"));
const EmployeeProfilePage = React.lazy(() => import("./components/hr/employees/EmployeeProfilePage"));
const TimeTrackingPage = React.lazy(() => import("./components/hr/time/TimeTrackingPage"));
const TimeEntriesTable = React.lazy(() => import("./components/hr/time/TimeEntriesTable"));
const TimesheetsPage = React.lazy(() => import("./components/hr/time/TimesheetsPage"));
const TimesheetDetailPage = React.lazy(() => import("./components/hr/time/TimesheetDetailPage"));
const OnboardingDashboard = React.lazy(() => import("./components/hr/onboarding/OnboardingDashboard"));
const OnboardingTemplatesPage = React.lazy(() => import("./components/hr/onboarding/OnboardingTemplatesPage"));
const TemplateBuilderPage = React.lazy(() => import("./components/hr/onboarding/TemplateBuilderPage"));
const WorkflowDetailPage = React.lazy(() => import("./components/hr/onboarding/WorkflowDetailPage"));
const HRDocumentsPage = React.lazy(() => import("./components/hr/documents/HRDocumentsPage"));
const DocumentEditorPage = React.lazy(() => import("./components/hr/documents/DocumentEditorPage"));
const DocumentViewPage = React.lazy(() => import("./components/hr/documents/DocumentViewPage"));
const PayrollComingSoon = React.lazy(() => import("./components/hr/PayrollComingSoon"));
// Athlete Monitoring
const MonitoringOverviewPage = React.lazy(() => import("./components/monitoring/MonitoringOverviewPage"));
const ManageSkillsPage = React.lazy(() => import("./components/monitoring/ManageSkillsPage"));
const AthleteAssessmentPage = React.lazy(() => import("./components/monitoring/AthleteAssessmentPage"));
const AthleteGoalsPage = React.lazy(() => import("./components/monitoring/AthleteGoalsPage"));
// Integrations Settings
const IntegrationsSettingsPage = React.lazy(() => import("./components/admin/settings/IntegrationsSettings/IntegrationsSettingsPage"));

/**
 * LegacyRedirect - Handles redirects from old routes to new routes
 * Properly substitutes URL parameters (e.g., :membershipId, :athleteId)
 */
const LegacyRedirect: React.FC<{ to: string }> = ({ to }) => {
  const params = useParams();
  const location = useLocation();

  // Replace all route parameters in the target path
  let targetPath = to;
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      targetPath = targetPath.replace(`:${key}`, value);
    }
  });

  // Preserve query string
  const queryString = location.search;

  return <Navigate to={`${targetPath}${queryString}`} replace />;
};

function AppContent() {
  const navigate = useNavigate();
  const token = safeGetItem("authToken");
  const isAdmin = useSelector(selectIsAdmin);
  const isStaff = useSelector(selectIsStaff);
  const isMember = useSelector(selectIsMember);
  const dispatch = useDispatch();
  const location = useLocation();
  const currentPath = location.pathname;
  const role = safeGetItem("userRole");
  const { isDarkMode } = useTheme();
  const [tenantSupabaseReady, setTenantSupabaseReady] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);

  // Rate limiting for Supabase events (prevent rapid-fire notifications)
  const lastSupabaseEventRef = useRef<number>(0);
  const SUPABASE_EVENT_COOLDOWN_MS = 5000; // 5 seconds between events

  // Version checking for update detection (complements Supabase real-time events)
  // This catches updates when users load the app after a deployment
  const {
    hasUpdate,
    reloadToUpdate,
    dismissUpdate,
    snoozeUpdate,
    latestVersion,
    isOffline,
    isForceUpdate,
    forceUpdateMessage,
  } = useAppVersion({
    pollInterval: 5 * 60 * 1000, // Check every 5 minutes (increased from 3)
    enablePolling: true,
    onNewVersionDetected: (newVersion, currentVersion) => {
      // Check if in snooze period (user clicked "Remind me later")
      try {
        const snoozeUntil = safeGetItem('app_update_snooze_until');
        if (snoozeUntil) {
          const snoozeUntilTime = parseInt(snoozeUntil, 10);
          if (!isNaN(snoozeUntilTime) && Date.now() < snoozeUntilTime) {
            return;
          }
        }
      } catch {
        // localStorage might be disabled, continue
      }

      // Check if in dismissal cooldown (30 minutes after dismissing)
      try {
        const cooldownTime = safeGetItem('app_update_dismissal_time');
        if (cooldownTime) {
          const cooldownTimestamp = parseInt(cooldownTime, 10);
          // If localStorage is corrupted (NaN), don't enforce cooldown
          if (!isNaN(cooldownTimestamp)) {
            const timeSinceDismissal = Date.now() - cooldownTimestamp;
            if (timeSinceDismissal < 30 * 60 * 1000) {
              return;
            }
          }
        }
      } catch {
        // localStorage might be disabled, continue without cooldown check
      }

      // Check legacy dismissal flag (user clicked X to close notification)
      try {
        if (safeGetItem('appUpdateDismissed')) {
          return;
        }
      } catch {
        // localStorage might be disabled, continue
      }

      // Check if this specific gitHash was already dismissed
      try {
        const dismissedGitHash = safeGetItem('app_update_dismissed_version');
        if (dismissedGitHash === newVersion.gitHash) {
          return;
        }
      } catch {
        // localStorage might be disabled, continue
      }

      // Set the flag so the existing notification system picks it up
      // Note: The useEffect watching hasUpdate will handle showing the notification
      try {
        safeSetItem('appUpdateRequested', 'true');
        safeSetItem('appUpdateRequestedGitHash', newVersion.gitHash);
        // Also store full version data for dismissal fallback (consistent with Supabase handler)
        safeSetItem('appUpdateRequestedVersionData', JSON.stringify(newVersion));
      } catch (err) {
        // localStorage error - non-critical
      }
    },
  });

  // Extract tenant ID dynamically from current URL
  const pathParts = location.pathname.split("/").filter(Boolean);
  const tenantId = pathParts[0] || null;

  // Restore Redux state from localStorage on app mount/reload
  useEffect(() => {
    const role = safeGetItem("userRole");
    safeGetItem("authSource");
    const userDetails = safeGetItem("userDetails");
    const savedSettings = safeGetItem("settings");

    if (role) {
      // isAdmin is deliberately NOT set here. It is written ONLY by
      // setAuthzFromCapabilities (the server's answer, resolved from role_id)
      // or cleared to false on logout. Deriving it from the localStorage role
      // STRING made state.auth.isAdmin have two writers racing each other, and
      // the string reads users.role — the drifted column the API does not
      // authorize on. On production that disagreed with the server for 5 of 14
      // staff accounts, in both directions.
      //
      // Until capabilities load, isAdmin stays false: admin UI appears a beat
      // late rather than appearing for someone the API will refuse. Failing
      // closed is the only safe direction for a gate.
      dispatch(setIsStaff(role)); // role text IS reliable for staff-vs-member
      dispatch(setIsMember(role));
    }

    if (userDetails) {
      try {
        const parsedUser = JSON.parse(userDetails);
        dispatch(setUser(parsedUser));
      } catch (error) {
        captureError(error, { component: 'AppContent', operation: 'parseUserDetails' });
      }
    }

    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        dispatch(setSettings(parsedSettings));
        applyCSSVariables(parsedSettings);
        applyDocumentChrome(parsedSettings);
        setTenantBranding(parsedSettings);
      } catch (error) {
        captureError(error, { component: 'AppContent', operation: 'parseSettings' });
      }
    }
  }, []); // Run only once on mount

  useEffect(() => {
    // Ensure the token exists before making the API call
    if (!token) {
      return; // Skip if no token is available
    }

    // Call Members, Athletes, and Staff API if user is admin
    if (role === "admin") {
      const fetchAllUserTypes = async () => {
        try {
          // allSettled: these populate INDEPENDENT Redux stores. Under
          // Promise.all a single rejection skipped every dispatch, so one
          // failing lookup silently blanked memberships AND athletes together.
          //
          // The staff directory used to be fetched here too, on boot, for
          // every logged-in user including members — into a store that
          // nothing ever read (allStaff had no selector callers). Dropped
          // rather than left as a request members had no reason to make.
          const [membersSt, athletesSt] = await Promise.allSettled([
            getAllMemberships(token, {}),
            getAllAthletes(token, {}),
          ]);
          for (const r of [membersSt, athletesSt]) {
            if (r.status === "rejected") {
              captureError(r.reason, { component: "App", operation: "fetchAllUserTypes" }, "warning");
            }
          }
          const membersRes = membersSt.status === "fulfilled" ? membersSt.value : undefined;
          const athletesRes = athletesSt.status === "fulfilled" ? athletesSt.value : undefined;

          if (membersRes?.status === "success") {
            dispatch(setMembershipsList(membersRes?.data));
          }
          if (athletesRes?.status === "success") {
            dispatch(setAllAthletes(athletesRes?.data));
          }
        } catch (err) {
          // Was empty under "Handled by axios interceptor" — the interceptor
          // does not repopulate these stores or tell anyone they are empty.
          captureError(err, { component: "App", operation: "fetchAllUserTypes" }, "warning");
        }
      };

      fetchAllUserTypes();
    }
    // NOTE: Monthly + annual auto-charging is handled entirely by the backend
    // crons (runScheduledPayments / runMonthlyBilling in backend/src/index.js).
    // The old client-side chargeAutomaticPayments/chargeAutomaticAnnualFees
    // triggers were removed 2026-06-10: they fired a real charge sweep from
    // every browser tab on load + a 24h interval, operated on the now-empty
    // legacy athlete_monthly_payments table (collected nothing), and 500'd on
    // tenants without Stripe configured. Charging is backend-only by design.
  }, [token]);

  // Fetch permissions for a specific role
  const fetchRolePermissions = async () => {
    if (!token) {
      return; // Skip if no token is available
    }
    try {
      // Ask the server what THIS caller may do. It resolves from role_id, the
      // field the API actually authorizes on. The fallback below matches the
      // drifted users.role TEXT against the all-roles map, which disagreed
      // with the server for 5 of the 14 staff accounts on production.
      const caps = await getCapabilities();
      if (caps) {
        dispatch(setAuthzFromCapabilities(caps));
        return;
      }

      // 1️ Fetch role-specific permissions
      const response = await getRolesWithPermissions(token);
      // 2 If successful, update Redux and refresh token in localStorage
      if (response?.success) {
        // 2️⃣ Get the current role (from localStorage or Redux)
        const userRole: any = safeGetItem("userRole"); // e.g., "admin" | "staff"

        // 3️⃣ Extract permissions for that role. Case-insensitive on purpose —
        // an exact lookup misses any role whose stored name isn't already
        // all-lowercase and returns [], locking the user out of every gated
        // page. See selectPermissionsForRole.
        const rolePermissions = selectPermissionsForRole<any>(
          response.data,
          userRole
        );

        // 4️⃣ Save into Redux
        dispatch(setPermissions(rolePermissions));

        // 5️⃣ Update token if present.
        //
        // Through persistAuthToken, never straight to storage. This wrote the
        // response's freshly signed token directly, and the effect below
        // depends on `token` — which is read from storage on every render. The
        // backend mints a new token on EVERY response, so each write changed
        // the string, re-ran this effect, and wrote again: an infinite loop at
        // network speed, ~1-3 iterations a second, dragging every other
        // `[token]` effect in the app along with it.
        //
        // It only bites when getCapabilities() resolves falsy (e.g. a 304 with
        // no body) and execution reaches this fallback, which is why one
        // session floods while another is idle. 2026-09-14, Gymnastics of
        // York: ~6 req/s for 90 minutes; the club could not use the app.
        // persistAuthToken skips a same-user refresh while the stored token is
        // fresh, which breaks the cycle.
        if (response?.token) {
          persistAuthToken(response.token);
        }
      }
    } catch (error) {
      // Handled by axios interceptor
    }
  };

  // Initialize roles and permissions on component mount
  useEffect(() => {
    fetchRolePermissions();
  }, [token]);

  useEffect(() => {
    // Extract tenant from current URL path
    const pathParts = location.pathname.split("/").filter(Boolean);
    let currentTenant = pathParts[0] || null;

    // Reject invalid tenant slugs like literal "null" or "undefined"
    const invalidSlugs = ['null', 'undefined', 'false', 'true'];
    if (currentTenant && invalidSlugs.includes(currentTenant.toLowerCase())) {
      // Redirect to root instead of proceeding with invalid tenant
      try {
        safeRemoveItem("tenantID");
        safeRemoveItem("settings");
      } catch { /* localStorage may be blocked in iframe */ }
      navigate("/", { replace: true });
      return;
    }

    // List of known route paths that are NOT tenant slugs
    const knownRoutes = [
      "login",
      "register",
      "programs",
      "forgot-password",
      "reset-password",
      "magic-login",
      "about",
      "contact",
      "faq",
      "privacy",
      "privacy-policy",
      "terms",
      "terms-of-service",
      "cookie-policy",
      "eula",
      "support",
      "accessibility-policy",
      "dashboard",
      "profile",
      "settings",
      "members",
      "staff",
      "teams",
      "athletes",
      "coaches",
      "admin",
      "billing",
      "reports",
      "memberships",
      "invoices",
      "kiosk",
    ];

    // Reset ready state when tenant changes
    setTenantSupabaseReady(false);
    setShowMaintenance(false);

    // Skip initialization if no tenant in path (e.g., on root search page)
    if (!currentTenant) {
      // Clear tenant data if navigating to root search page
      try {
        safeRemoveItem("tenantID");
        safeRemoveItem("settings");
      } catch { /* localStorage may be blocked in iframe */ }
      setTenantSupabaseReady(true); // No tenant needed, ready to proceed
      return;
    }

    // Apex public routes — accessible without a tenant slug. Render
    // immediately so external reviewers (Twilio TFV, app-store review)
    // and footer-link visitors with a fresh browser can reach the page
    // without being routed through tenant init or bounced to the
    // search page. Must mirror the apex `<Route path="/...">` entries
    // near the bottom of this file.
    const apexPublicRoutes = [
      "sms-opt-in",
      "privacy",
      "privacy-policy",
      "terms",
      "terms-of-service",
    ];
    if (apexPublicRoutes.includes(currentTenant)) {
      setTenantSupabaseReady(true);
      return;
    }

    // Check if the current path segment is a known route (not a tenant)
    if (knownRoutes.includes(currentTenant)) {
      let storedTenantSlug = null;
      try {
        storedTenantSlug = safeGetItem("tenantSlug");
      } catch { /* localStorage may be blocked in iframe */ }
      if (storedTenantSlug) {
        // User navigated to /login but should be at /tenant/login
        // Redirect to include the tenant slug
        navigate(`/${storedTenantSlug}${location.pathname}`, { replace: true });
        return;
      } else {
        // No stored tenant, redirect to root search page
        navigate("/", { replace: true });
        return;
      }
    }

    // Check if we're switching to a different tenant
    let storedTenantSlug2 = null;
    try {
      storedTenantSlug2 = safeGetItem("tenantSlug");
    } catch { /* localStorage may be blocked in iframe */ }
    if (storedTenantSlug2 && storedTenantSlug2 !== currentTenant) {
      // Clear stale tenant data when switching tenants
      try {
        safeRemoveItem("tenantID");
        safeRemoveItem("settings");
        safeRemoveItem("authToken");
        safeRemoveItem("userRole");
        safeRemoveItem("userDetails");
        safeRemoveItem("authSource");
      } catch { /* localStorage may be blocked in iframe */ }
    }

    // For embed routes, skip full tenant validation/redirect — the tenant ID is in the URL
    // and embed iframes may have localStorage blocked (third-party context)
    const pathAfterTenant = pathParts[1] || '';
    if (pathAfterTenant === 'embed') {
      initializeTenantSupabase()
        .then(() => setTenantSupabaseReady(true))
        .catch(() => setTenantSupabaseReady(true));
      return;
    }

    // Supabase init and tenant validation are combined in initAndValidate() below

    // get tenant
    async function fetchTenant() {
      // Probe that the tenant exists / the backend is reachable — this boolean
      // feeds the maintenance-page + redirect logic below (alongside settings).
      // The tenant slug lives in the URL (+ localStorage tenantSlug) and the
      // backend resolves the UUID itself, so the frontend no longer stores a
      // separate tenant id here.
      // MUST NOT reject. The boolean it returns IS the maintenance-mode signal
      // (see validateTenant below), and this runs inside a Promise.all whose
      // rejection would skip setTenantSupabaseReady(true) and hang every
      // authenticated route on a spinner forever. Before getTenant rethrew, a
      // failure arrived here as `undefined` and fell out as false; now it
      // arrives as a throw, so it has to be converted back explicitly.
      try {
        const res = await getTenant();
        return !!res?.success;
      } catch (err) {
        captureError(err, { component: "App", operation: "fetchTenant" }, "warning");
        return false;
      }
    }

    // get settings
    async function fetchSettings() {
      // Same contract as fetchTenant: must resolve to a boolean, never reject.
      try {
        const response = await getSettings();
        if (response?.success) {
          try { safeSetItem("settings", JSON.stringify(response?.data)); } catch { /* iframe */ }
          dispatch(setSettings(response?.data));
          applyCSSVariables(response?.data);
          applyDocumentChrome(response?.data);
          setTenantBranding(response?.data);
          return true;
        }
        return false;
      } catch (err) {
        captureError(err, { component: "App", operation: "fetchSettings" }, "warning");
        return false;
      }
    }

    // Validate tenant exists by attempting to fetch data
    async function validateTenant() {
      const [tenantExists, settingsExist] = await Promise.all([
        fetchTenant(),
        fetchSettings()
      ]);

      // If both tenant and settings fetch failed, the tenant may be invalid or services are down
      if (!tenantExists && !settingsExist) {
        let lastValidTenant = null;
        try {
          lastValidTenant = safeGetItem("tenantSlug");
        } catch {
          // localStorage may be blocked in third-party iframe contexts
        }

        // If the current URL tenant matches a previously valid tenant,
        // show maintenance page instead of redirecting to "Find Your Club"
        if (currentTenant && currentTenant === lastValidTenant) {
          setShowMaintenance(true);
          return;
        }

        if (lastValidTenant && lastValidTenant !== currentTenant) {
          // Redirect to the last valid tenant
          navigate(`/${lastValidTenant}`, { replace: true });
        } else {
          // No valid tenant in localStorage, redirect to root search page
          try {
            safeRemoveItem("tenantID");
            safeRemoveItem("tenantSlug");
            safeRemoveItem("settings");
          } catch {
            // localStorage may be blocked in third-party iframe contexts
          }
          navigate("/", { replace: true });
        }
      } else {
        // Validation succeeded! Save this as the valid tenant
        setShowMaintenance(false);
        if (currentTenant) {
          try {
            safeSetItem("tenantSlug", currentTenant);
          } catch {
            // localStorage may be blocked in third-party iframe contexts
          }
        }
      }
    }

    // Run Supabase init and tenant validation (settings fetch) in parallel.
    // getSettings() uses axios (not Supabase client), so no dependency between them.
    // Only show content after settings are loaded into Redux.
    async function initAndValidate() {
      // allSettled, and setTenantSupabaseReady(true) runs no matter what.
      //
      // App.tsx gates the ENTIRE authenticated render on !tenantSupabaseReady.
      // Under Promise.all with a bare invocation below, any rejection here left
      // that flag false forever — every route stuck on a spinner, with an
      // unhandled rejection as the only trace. Whatever fails, the app must
      // still be allowed to render and show its own error/maintenance state.
      const results = await Promise.allSettled([
        initializeTenantSupabase(),
        validateTenant()
      ]);
      for (const r of results) {
        if (r.status === "rejected") {
          captureError(r.reason, { component: "App", operation: "initAndValidate" }, "error");
        }
      }
      setTenantSupabaseReady(true);
    }
    // Belt and braces: initAndValidate cannot reject as written, but it is
    // bare-invoked, so any future throw inside it would be unhandled.
    initAndValidate().catch((err) => {
      captureError(err, { component: "App", operation: "initAndValidate" }, "error");
      setTenantSupabaseReady(true);
    });
  }, [location.pathname]);

  useEffect(() => {
    // One list, shared with its test: utils/publicClubPaths.
    const isPublic = isPublicClubPath(location.pathname);
    if (!isPublic && (!role || !token)) {
      // Use current tenantId, fall back to localStorage, or redirect to root
      const slug = tenantId || safeGetItem("tenantSlug");
      if (slug && slug !== "null" && slug !== "undefined") {
        // An emailed link to the member's Payment Settings should survive
        // the sign-in detour (allow-listed pages only).
        rememberPostLoginDestination(location.pathname, location.search);
        navigate(`/${slug}/login`, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [role, token, tenantId, navigate, location.pathname]);

  // Global sign-out listener for management system events
  // This listens for new rows in the 'system_events' table with the event_name 'GLOBAL_SIGN_OUT'
  useEffect(() => {
    // Only subscribe if the user is authenticated
    if (!token) return;

    // Check auth session
    supabase.auth.getSession()
      .catch(() => {
        // Handled by axios interceptor
      });

    const channel = supabase
      .channel('system-events-listener', {
        config: {
          broadcast: { self: true },
        },
      })
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_events',
          filter: 'event_name=eq.GLOBAL_SIGN_OUT',
        },
        (payload) => {
          handleGlobalSignOut();
        }
      )
      .subscribe(() => {
        // Channel status handled internally
      });

    // Cleanup function to remove the channel subscription when the component unmounts
    return () => {
      supabase.removeChannel(channel);
    };
  }, [token]); // Dependency array ensures this runs only when the token changes

  // Centralized global sign-out handler
  const handleGlobalSignOut = async () => {
    // Use centralized logout utility - clears all user data, preserves tenant info
    const tenantSlug = await performLogout();

    // Reset Redux state
    dispatch(setIsAdmin(false));
    dispatch(setIsStaff(false));
    dispatch(setIsMember(false));

    // Redirect to login (use preserved tenant slug or current tenantId)
    navigate(`/${tenantSlug || tenantId}/login`);
  };

  // Show app update notification toast
  const showAppUpdateNotification = useCallback((forceUpdate: boolean = false, forceMessage?: string) => {
    const toastId = 'app-update-notification';

    // For force updates, don't check dismissal - always show
    if (!forceUpdate) {
      // Get the gitHash that triggered this notification request
      let requestedGitHash: string | null = null;
      let dismissedGitHash: string | null = null;
      try {
        requestedGitHash = safeGetItem('appUpdateRequestedGitHash');
        dismissedGitHash = safeGetItem('app_update_dismissed_version');
      } catch {
        // localStorage might be disabled, continue without dismissal check
      }

      // Only show if this specific version wasn't already dismissed
      if (requestedGitHash && dismissedGitHash === requestedGitHash) {
        return;
      }

      // Also check the legacy dismissal flag
      try {
        if (safeGetItem('appUpdateDismissed')) {
          return;
        }
      } catch {
        // localStorage might be disabled, continue
      }
    }

    // Check if toast is already active to avoid duplicates
    if (toast.isActive(toastId)) {
      return;
    }

    // Brand color via CSS var; only used inside the toast button below.
    // Falls back via applyCSSVariables() default if the tenant hasn't picked one.

    // Force update uses red/error styling
    const updateTitle = forceUpdate ? '⚠️ Critical Update Required!' : 'Update Available!';
    const updateMessage = forceUpdate
      ? (forceMessage || 'A critical security update is required. Please reload immediately.')
      : 'A new version of the application is available. Please reload to get the latest features.';
    const toastType = forceUpdate ? toast.error : toast.info;

    toastType(
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        color: isDarkMode ? '#f3f4f6' : '#1f2937',
      }}>
        <strong>{updateTitle}</strong>
        <p style={{ margin: 0, color: isDarkMode ? '#d1d5db' : '#4b5563' }}>
          {updateMessage}
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              // Use the hook's reloadToUpdate which properly handles the reload
              // This sets JUST_RELOADED_FOR_UPDATE_KEY to prevent notification reappearing
              toast.dismiss(toastId);
              reloadToUpdate();
            }}
            className={`px-4 py-2 rounded text-sm font-bold border-0 cursor-pointer transition-colors ${
              forceUpdate
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-[var(--cc-action)] hover:bg-[var(--cc-action-hover)] text-[var(--cc-action-text)]'
            }`}
          >
            Reload Now
          </button>
          {/* Only show "Remind me later" for non-critical updates */}
          {!forceUpdate && (
            <button
              onClick={() => {
                // Set legacy dismissal flag directly (don't rely on onClose firing)
                try {
                  safeSetItem('appUpdateDismissed', 'true');
                } catch {
                  // Ignore localStorage errors
                }
                toast.dismiss(toastId);
                // snoozeUpdate now properly handles all dismissal state
                snoozeUpdate();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDarkMode ? '#4b5563' : '#e5e7eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#f3f4f6';
              }}
              style={{
                backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
                color: isDarkMode ? '#f3f4f6' : '#374151',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'background-color 0.2s ease',
              }}
            >
              Remind me later
            </button>
          )}
        </div>
      </div>,
      {
        toastId,
        autoClose: false,
        closeButton: !forceUpdate, // No close button for force updates
        position: 'top-center',
        closeOnClick: false,
        draggable: false,
        style: {
          backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
        },
        onClose: () => {
          // Force updates can't be dismissed via close button (it's hidden)
          // This handler only runs for regular updates
          try {
            // Mark as dismissed when user closes the toast
            safeSetItem('appUpdateDismissed', 'true');
            // Also save the gitHash to the hook's dismissal key for synchronization
            const requestedGitHash = safeGetItem('appUpdateRequestedGitHash');
            if (requestedGitHash) {
              safeSetItem('app_update_dismissed_version', requestedGitHash);
            }
            // CRITICAL: Update the stored version to match the server version
            // This prevents the hook from detecting a "new" version on subsequent polls
            // Try latestVersion from hook first, fall back to Supabase-stored version data
            if (latestVersion) {
              safeSetItem('app_version_info', JSON.stringify(latestVersion));
            } else {
              // Fallback: use version data stored by Supabase handler
              // This handles the case where Supabase triggers notification before hook polls
              const storedVersionData = safeGetItem('appUpdateRequestedVersionData');
              if (storedVersionData) {
                safeSetItem('app_version_info', storedVersionData);
              }
            }
            // Clean up the request flags
            safeRemoveItem('appUpdateRequested');
            safeRemoveItem('appUpdateRequestedGitHash');
            safeRemoveItem('appUpdateRequestedVersionData');
          } catch (err) {
            // localStorage might be full or unavailable
          }
          // CRITICAL: Call the hook's dismissUpdate to reset hasUpdate state
          // This prevents the notification from re-appearing on route changes
          // Always call this even if localStorage operations failed
          dismissUpdate();
        },
      }
    );
  }, [isDarkMode, latestVersion, reloadToUpdate, snoozeUpdate, dismissUpdate]);

  // Check on mount and when hasUpdate changes if update notification should be shown
  // This responds to both:
  // 1. Supabase real-time events (sets localStorage flags)
  // 2. useAppVersion polling (sets hasUpdate and localStorage flags)
  useEffect(() => {
    if (!token) return;

    // Force updates bypass all cooldown/dismissal logic
    if (isForceUpdate) {
      showAppUpdateNotification(true, forceUpdateMessage || undefined);
      return;
    }

    // Check if in dismissal cooldown (30 minutes after dismissing)
    try {
      const cooldownTime = safeGetItem('app_update_dismissal_time');
      if (cooldownTime) {
        const cooldownTimestamp = parseInt(cooldownTime, 10);
        // If localStorage is corrupted (NaN), don't enforce cooldown
        if (!isNaN(cooldownTimestamp)) {
          const timeSinceDismissal = Date.now() - cooldownTimestamp;
          if (timeSinceDismissal < 30 * 60 * 1000) {
            // Clean up any lingering flags
            try {
              safeRemoveItem('appUpdateRequested');
            } catch {
              // Ignore cleanup errors
            }
            return;
          }
        }
      }
    } catch {
      // localStorage might be disabled, continue without cooldown check
    }

    // Get dismissal states
    let updateRequested: string | null = null;
    let requestedGitHash: string | null = null;
    let dismissedGitHash: string | null = null;
    let legacyDismissed: string | null = null;
    try {
      updateRequested = safeGetItem('appUpdateRequested');
      requestedGitHash = safeGetItem('appUpdateRequestedGitHash');
      dismissedGitHash = safeGetItem('app_update_dismissed_version');
      legacyDismissed = safeGetItem('appUpdateDismissed');
    } catch {
      // localStorage might be disabled, continue without dismissal check
    }

    // Check if this specific version was already dismissed
    const isVersionDismissed = requestedGitHash && dismissedGitHash === requestedGitHash;

    // If already dismissed, clean up any lingering flags and exit
    if (isVersionDismissed || legacyDismissed) {
      // Clear the request flag since it's dismissed
      if (updateRequested) {
        try {
          safeRemoveItem('appUpdateRequested');
        } catch {
          // Ignore cleanup errors
        }
      }
      return;
    }

    // Check localStorage flags (set by Supabase events OR useAppVersion)
    if (updateRequested) {
      showAppUpdateNotification();
      return;
    }

    // Also trigger if useAppVersion detected an update (and not dismissed)
    if (hasUpdate) {
      showAppUpdateNotification();
    }
  }, [token, hasUpdate, isForceUpdate, forceUpdateMessage, showAppUpdateNotification]); // Only re-check when token or hasUpdate changes, NOT on route changes

  // Keyboard accessibility: Allow Escape key to dismiss update notification
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const toastId = 'app-update-notification';
        if (toast.isActive(toastId)) {
          toast.dismiss(toastId);
          // Note: The toast's onClose handler will call dismissUpdate()
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Realtime listener for application update notifications
  useEffect(() => {
    // Only subscribe if the user is authenticated
    if (!token) return;

    const channel = supabase
      .channel('app-update-listener')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_events',
          filter: 'event_name=eq.APP_RELOAD_REQUESTED',
        },
        async (payload) => {
          // Rate limit Supabase events to prevent rapid-fire notifications
          const now = Date.now();
          if (now - lastSupabaseEventRef.current < SUPABASE_EVENT_COOLDOWN_MS) {
            return;
          }
          lastSupabaseEventRef.current = now;

          // Check if user just reloaded for an update - the hook will handle updating stored version
          // This prevents a race condition where Supabase event fires before hook completes
          try {
            if (safeGetItem('app_just_reloaded_for_update') === 'true') {
              return;
            }
          } catch {
            // localStorage might be disabled (private browsing), continue with other checks
          }

          // Check if in dismissal cooldown (30 minutes after dismissing)
          let cooldownTime: string | null = null;
          try {
            cooldownTime = safeGetItem('app_update_dismissal_time');
          } catch {
            // localStorage might be disabled, skip cooldown check
          }
          if (cooldownTime) {
            const cooldownTimestamp = parseInt(cooldownTime, 10);
            // If localStorage is corrupted (NaN), don't enforce cooldown
            if (!isNaN(cooldownTimestamp)) {
              const timeSinceDismissal = Date.now() - cooldownTimestamp;
              if (timeSinceDismissal < 30 * 60 * 1000) {
                return;
              }
            }
          }

          // Wait briefly before fetching version.json
          // This gives CDN edges time to propagate the new file after deployment
          await new Promise(resolve => setTimeout(resolve, 2000));

          // Fetch the current version.json to get the gitHash
          try {
            // Create abort controller for timeout (10 seconds)
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);

            const response = await fetch(`/version.json?_cb=${Date.now()}`, {
              method: 'GET',
              headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
              },
              signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
              return; // Don't show notification if we can't verify the version
            }

            const versionData = await response.json();

            // Validate response has expected structure
            if (!versionData || typeof versionData.gitHash !== 'string') {
              return;
            }

            const newGitHash = versionData.gitHash;

            if (!newGitHash) {
              return;
            }

            // Check if user already has this version (stored version matches server)
            try {
              const storedVersionStr = safeGetItem('app_version_info');
              if (storedVersionStr) {
                try {
                  const storedVersion = JSON.parse(storedVersionStr);
                  if (storedVersion.gitHash === newGitHash) {
                    return; // User already has the new version, no need to notify
                  }
                } catch {
                  // Ignore parse errors, continue with other checks
                }
              }
            } catch {
              // localStorage might be disabled, continue with other checks
            }

            // Check if this version was already dismissed
            try {
              const dismissedGitHash = safeGetItem('app_update_dismissed_version');
              if (dismissedGitHash === newGitHash) {
                return;
              }
            } catch {
              // localStorage might be disabled, continue
            }

            // Check legacy dismissal flag
            try {
              if (safeGetItem('appUpdateDismissed')) {
                return;
              }
            } catch {
              // localStorage might be disabled, continue
            }

            // Store the gitHash for dismissal tracking (best effort)
            try {
              safeSetItem('appUpdateRequestedGitHash', newGitHash);

              // IMPORTANT: Also store the full version data for use when dismissing
              // This ensures dismissUpdate() can update app_version_info even if the hook
              // hasn't fetched version.json yet (fixes false-positive after Supabase dismiss)
              safeSetItem('appUpdateRequestedVersionData', JSON.stringify(versionData));

              // Mark that an update was requested
              safeSetItem('appUpdateRequested', 'true');
            } catch {
              // localStorage might be full or disabled, but still show notification
            }

            // Show the notification
            showAppUpdateNotification();
          } catch (err) {
            // Don't show notification if we can't verify the version
          }
        }
      )
      .subscribe(() => {
        // Channel status handled internally
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [token]); // Dependency array ensures this runs only when the token changes

  // Show maintenance page when services are down but tenant is known
  if (showMaintenance) {
    const clubName = safeGetItem("clubName") || "Our platform";
    return (
      <div className={`flex justify-center items-center min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
        <div className={`text-center max-w-md p-8 rounded-lg shadow-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <svg className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h1 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            Temporary Service Disruption
          </h1>
          <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {clubName} is experiencing temporary issues. Please try again shortly.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show loading indicator while tenant Supabase is initializing
  // Allow public routes to render immediately for faster FCP
  if (!tenantSupabaseReady) {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const routeSegment = pathParts[1] || '';
    const publicRouteSegments = [
      'login', 'register', 'kiosk', 'trial', 'forgot-password',
      'reset-password', 'magic-login', 'programs', 'about', 'contact',
      'faq', 'privacy', 'privacy-policy', 'terms', 'terms-of-service',
      'cookie-policy', 'eula', 'support', 'accessibility-policy', 'members-summary', 'posts',
      'events', 'camps', 'clinics', 'due-payments', 'embed', 'shared',
      'accept-invite', 'profile', 'd', 'sign-waiver',
    ];
    if (!publicRouteSegments.includes(routeSegment)) {
      return (
        <div className={`flex justify-center items-center min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto mb-4"></div>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading...</p>
          </div>
        </div>
      );
    }
  }

  return (
    <AuthProvider>
      <TenantProvider>
        <PermissionProvider>
        <EntitlementProvider>
        <CartProvider>
          <OfflineQueueProvider
            patchFunctions={{
              membership: (id, updates) => patchMembershipDetails(token, id, updates),
              athlete: (id, updates) => patchAthlete(token, id, updates),
            }}
          >
            <NotificationProvider>
            <ChatProvider>
            {/* Rings staff who answer calls when the AI receptionist hands a
                caller over. Mounted once above the routes so a live call
                survives navigation; inert for everyone else. */}
            <CallCenterProvider
              enabled={(isAdmin || isStaff) && !currentPath.includes('/kiosk') && !currentPath.includes('/embed/')}
            >
            {/* Update-available banner — Vaeth + -3K stale-bundle pattern
                2026-06-05. Toast-based notification can be silenced
                (dismiss / snooze / cooldown). Banner is the persistent
                backstop; surfaces every time hasUpdate is true unless
                the user explicitly snoozes for 15min. Sits above the
                offline banner because update info is more actionable. */}
            <UpdateAvailableBanner
              hasUpdate={hasUpdate}
              isForceUpdate={isForceUpdate}
              forceUpdateMessage={forceUpdateMessage}
              onReload={reloadToUpdate}
              onSnooze={snoozeUpdate}
            />
            {/* Offline indicator banner */}
            {isOffline && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 9999,
                  backgroundColor: '#f59e0b',
                  color: '#1f2937',
                  padding: '8px 16px',
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="1" y1="1" x2="23" y2="23" />
                  <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
                  <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
                  <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
                  <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
                  <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                  <line x1="12" y1="20" x2="12.01" y2="20" />
                </svg>
                You are currently offline. Some features may be unavailable.
              </div>
            )}
            <ScrollToTop />
            {/* Help-article links: scroll to #anchor, open its section, highlight it. */}
            <HelpAnchorScroller />
            <AnalyticsTracker />
            <Suspense fallback={
              <div className={`flex justify-center items-center min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto mb-4"></div>
                  <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading...</p>
                </div>
              </div>
            }>
            <Routes>
          {/* Public routes */}
          <Route path="/:clubid/login" element={<Login />} />
          <Route path="/:clubid/accept-invite/:token" element={<AcceptMembershipInvitePage />} />
          <Route path="/:clubid/kiosk" element={<KioskMode />} />
          <Route path="/:clubid/register" element={<Membership />} />
          <Route path="/:clubid/trial" element={<TrialSignup />} />
          <Route path="/:clubid/feedback/:token" element={<PublicFeedbackPage />} />
          {/* Emailed "please sign" link. The parent is usually not signed in. */}
          <Route path="/:clubid/sign-waiver" element={<SignWaiverPage />} />
          <Route path="/:clubid/profile/support-pin/reset" element={<SupportPinResetPage />} />
          {/* "Cancel here" link in volunteer signup emails; the token is the credential. */}
          <Route path="/:clubid/events/:eventId/volunteer-cancel" element={<VolunteerCancelPage />} />
          <Route path="/:clubid/d/:token" element={<PublicDesignViewerPage />} />
          <Route path="/:clubid/forgot-password" element={<ForgotPassword />} />
          <Route path="/:clubid/reset-password" element={<ResetPassword />} />
          <Route path="/:clubid/magic-login" element={<MagicLogin />} />
          {/* Athlete signin — public name search + PIN login (no auth);
              progress page reads athlete_token from localStorage and
              handles its own redirect to /athlete/login if missing. */}
          <Route path="/:clubid/athlete/login" element={<AthleteLoginPage />} />
          <Route path="/:clubid/athlete/progress" element={<AthleteProgressPage />} />
          <Route
            path="/:clubid/members-summary"
            element={<MembershipSummary />}
          />
          <Route path="/:clubid/programs" element={<ClubProgramsPage />} />
          <Route path="/:clubid/find" element={<ProgramFinder />} />
          <Route path="/:clubid/about" element={<AboutPage />} />
          <Route path="/:clubid/contact" element={<ContactPage />} />
          <Route path="/:clubid/faq" element={<FAQ />} />
          <Route path="/:clubid/privacy" element={<PrivacyPolicy />} />
          <Route path="/:clubid/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/:clubid/terms" element={<TermsOfService />} />
          <Route
            path="/:clubid/terms-of-service"
            element={<TermsOfService />}
          />
          <Route path="/:clubid/cookie-policy" element={<CookiePolicy />} />
          <Route path="/:clubid/eula" element={<Eula />} />
          <Route path="/:clubid/support" element={<Support />} />
          <Route
            path="/:clubid/accessibility-policy"
            element={<AccessibilityPolicy />}
          />
          {/* Public Events list - the target of "Back to events", the event
              breadcrumb and the sitemap (it was reachable only as an embed). */}
          <Route path="/:clubid/events" element={<PublicEventsListPage />} />
          {/* Public Event Page - shareable event details for non-members */}
          <Route path="/:clubid/events/:eventId" element={<PublicEventPage />} />
          {/* Public Camp Pages - listing and detail for non-members */}
          <Route path="/:clubid/camps" element={<PublicCampsListPage />} />
          <Route path="/:clubid/camps/:campId" element={<PublicCampPage />} />
          {/* Public Clinic Pages - listing and detail for non-members */}
          <Route path="/:clubid/clinics" element={<PublicClinicsListPage />} />
          <Route path="/:clubid/clinics/:clinicId" element={<PublicClinicPage />} />
          {/* Public Facility Booking - non-member party rentals etc */}
          <Route path="/:clubid/book" element={<PublicFacilityPage />} />
          {/* Public Blog - listing page */}
          <Route path="/:clubid/posts" element={<PublicPostsPage />} />
          {/* Public Post Page - view a published blog post */}
          <Route path="/:clubid/posts/:slug" element={<ViewPostPage />} />
          {/* Public web version of a sent broadcast — the link target for SMS
              and social derivatives. show_as_news covers the in-app case, but
              that is behind auth and unreachable for a marketing contact. */}
          <Route path="/:clubid/m/:slug" element={<PublicBroadcastPage />} />
          {/* Shared media link (public, no auth) */}
          <Route path="/:clubid/shared/:token" element={<React.Suspense fallback={<div />}><SharedMediaPage /></React.Suspense>} />

          {/* Embed routes - minimal layout for external iframes */}
          <Route path="/:clubid/embed/register" element={<EmbedRegistration />} />
          <Route path="/:clubid/embed/camps" element={<EmbedCamps />} />
          <Route path="/:clubid/embed/camps/:campId" element={<EmbedCampDetail />} />
          <Route path="/:clubid/embed/clinics" element={<EmbedClinics />} />
          <Route path="/:clubid/embed/clinics/:clinicId" element={<EmbedClinicDetail />} />
          <Route path="/:clubid/embed/events" element={<EmbedEvents />} />
          <Route path="/:clubid/embed/events/:eventId" element={<EmbedEventDetail />} />
          <Route path="/:clubid/embed/contact" element={<EmbedContact />} />

          {/* Tenant Search Page - Show when no tenant ID */}
          <Route path="/" element={<TenantSearchPage />} />

          {/* Apex public legal/disclosure pages — accessible without a
              tenant slug. Used by external reviewers (Twilio TFN
              verification, app store review, etc.) and as a stable
              link target from email footers, privacy policies, etc. */}
          <Route path="/sms-opt-in" element={<SmsOptInPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />

          {/* Redirect to default page based on role */}
          <Route
            path="/:clubid/*"
            element={
              isAdmin || isStaff ? (
                <Navigate to={`/${tenantId}/manage/memberships`} replace />
              ) : isMember ? (
                // ✅ Exception: Allow /member/invoice to pass through
                currentPath === `/${tenantId}/member/invoice` ? (
                  <MemberInvoice />
                ) : (
                  <Navigate to={`/${tenantId}/dashboard`} replace />
                )
              ) : (
                <LandingPage />
              )
            }
          />

          {/* ============================================
              UNIFIED MANAGE ROUTES (Admin & Staff)
              ============================================
              - Routes with adminOnly: true are only accessible to admins
              - All other routes are accessible to both admin and staff
              - Uses /manage/* paths for role-agnostic URLs
          */}
          {(isAdmin || isStaff) && (
            <>
              {[
                // ============================================
                // MEMBERSHIPS
                // ============================================
                {
                  path: "/:clubid/manage/memberships/add",
                  element: <RegisterMember />,
                  permission: "add_membership_page",
                },
                {
                  // Staff/admin dashboard landing — daily tip card + future
                  // widgets. Default home for new tenants (the existing
                  // /:clubid/dashboard route is the member dashboard).
                  path: "/:clubid/manage/dashboard",
                  element: <StaffDashboard />,
                },
                {
                  // Config Review walker — paginates through curated
                  // groups of settings / email templates / waivers per
                  // the kind in the URL. Phase 2 of the Config Health
                  // Checkup feature.
                  path: "/:clubid/manage/config-review/:kind",
                  permission: "page_config_review",
                  element: <ConfigReviewPage />,
                },
                {
                  path: "/:clubid/manage/memberships",
                  element: <ManageMembershipsTable />,
                  permission: "show_memberships_page",
                },
                {
                  path: "/:clubid/manage/memberships/invoices",
                  element: <ManageInvoiceTable />,
                  permission: "view_membership_invoices",
                },
                {
                  path: "/:clubid/manage/memberships/reassign",
                  element: <ReassignAthletesPage />,
                  permission: "page_memberships_reassign",
                },
                {
                  // URL-addressable single-membership editor. Reads
                  // :membershipId via useParams, hydrates Redux, then
                  // renders EditMembershipPage. Lets any deep-link to a
                  // specific membership work (previously fell through
                  // to the * catch-all and dumped the user on
                  // /{tenantId}). Listed AFTER the more-specific
                  // /invoices and /reassign paths so those match first.
                  path: "/:clubid/manage/memberships/:membershipId",
                  element: <MembershipDetailPage />,
                  permission: "show_memberships_page",
                },
                {
                  path: "/:clubid/manage/data-health",
                  element: <DataHealthPage />,
                  permission: "page_data_health",
                },
                {
                  path: "/:clubid/manage/facilities",
                  element: <FacilityManagePage />,
                  permission: "page_facilities",
                },
                {
                  path: "/:clubid/manage/scheduled-cancellations",
                  element: <ScheduledCancellationsPage />,
                  permission: "page_scheduled_cancellations",
                },

                // ============================================
                // CUSTOMERS
                // ============================================
                {
                  path: "/:clubid/manage/customers",
                  permission: "page_customers",
                  element: <ManageCustomersTable />,
                },
                {
                  path: "/:clubid/manage/customers/:customerId",
                  permission: "page_customers",
                  element: <EditCustomerPage />,
                },

                // ============================================
                // ATHLETES
                // ============================================
                {
                  path: "/:clubid/manage/athletes",
                  permission: "page_athletes",
                  element: <StaffAthleteTable />,
                },
                {
                  path: "/:clubid/manage/athletes/add",
                  permission: "page_athletes",
                  element: <AddAthlete />,
                },
                {
                  path: "/:clubid/manage/athletes/edit/:athleteId",
                  element: <StaffUpdateAthlete />,
                  permission: "manage_athletes_page",
                },
                {
                  // Staff review of member reinstatement requests —
                  // approve / move / reject the pending enrollments.
                  path: "/:clubid/manage/athletes/pending-reactivations",
                  element: <PendingReactivationsPage />,
                  permission: "manage_athletes_page",
                },
                {
                  path: "/:clubid/manage/teams",
                  permission: "page_teams",
                  element: <ManageTeamsPage />,
                },
                {
                  path: "/:clubid/manage/levels",
                  permission: "page_levels",
                  element: <ManageLevelsPage />,
                },

                // ============================================
                // ATHLETE MONITORING
                // ============================================
                {
                  path: "/:clubid/manage/monitoring",
                  permission: "page_monitoring",
                  element: <MonitoringOverviewPage />,
                },
                {
                  path: "/:clubid/manage/monitoring/skills",
                  element: <ManageSkillsPage />,
                  permission: "page_monitoring_skills",
                },
                {
                  path: "/:clubid/manage/athletes/:athleteId/assessments",
                  permission: "page_monitoring",
                  element: <AthleteAssessmentPage />,
                },
                {
                  path: "/:clubid/manage/athletes/:athleteId/goals",
                  permission: "page_monitoring",
                  element: <AthleteGoalsPage />,
                },

                // ============================================
                // ATTENDANCE
                // ============================================
                {
                  path: "/:clubid/manage/attendance",
                  element: <AttendancePage />,
                  permission: "mark_attendance_page",
                },
                {
                  path: "/:clubid/manage/attendance/absences",
                  element: <RecentAbsencePage />,
                  permission: "mark_attendance_page",
                },
                {
                  path: "/:clubid/manage/attendance/notes",
                  element: <AttendanceNotesPage />,
                  permission: "mark_attendance_page",
                },

                // ============================================
                // TASKS
                // ============================================
                {
                  path: "/:clubid/manage/tasks",
                  permission: "page_tasks",
                  element: <MyTasksPage />,
                  // No adminOnly - accessible to both admin and staff
                },

                // ============================================
                // PROGRAMS (Admin Only)
                // ============================================
                {
                  path: "/:clubid/manage/programs",
                  element: <ProgramsPage />,
                  permission: "page_programs",
                },
                {
                  path: "/:clubid/manage/programs/create",
                  element: <CreateProgramPage />,
                  permission: "page_programs",
                },
                {
                  path: "/:clubid/manage/programs/categories",
                  element: <ManageCategoriesPage />,
                  permission: "page_programs_categories",
                },

                // ============================================
                // MARKETING
                // ============================================
                {
                  path: "/:clubid/manage/marketing/broadcasts",
                  permission: "page_marketing_broadcasts",
                  element: <AllBroadcastsPage />,
                },
                {
                  path: "/:clubid/manage/marketing/broadcasts/new",
                  element: <BroadcastWizard />,
                  permission: "group_email",
                },
                {
                  path: "/:clubid/manage/marketing/broadcasts/:broadcastId",
                  permission: "page_marketing_broadcasts",
                  element: <BroadcastDetailPage />,
                },
                {
                  path: "/:clubid/manage/marketing/contacts",
                  permission: "page_marketing_contacts",
                  element: <ContactsPage />,
                },
                {
                  path: "/:clubid/manage/marketing/local-seo",
                  element: (
                    <ModuleGate moduleKey="marketing_growth_suite">
                      <LocalSeoPage />
                    </ModuleGate>
                  ),
                  permission: "page_marketing_local_seo",
                },
                {
                  path: "/:clubid/manage/marketing/advertising-intelligence",
                  element: (
                    <ModuleGate moduleKey="marketing_growth_suite">
                      <AdvertisingIntelligencePage />
                    </ModuleGate>
                  ),
                  permission: "page_marketing_advertising_intelligence",
                },
                {
                  path: "/:clubid/manage/marketing/reputation",
                  element: (
                    <ModuleGate moduleKey="marketing_growth_suite">
                      <ReputationAiPage />
                    </ModuleGate>
                  ),
                  permission: "page_marketing_reputation",
                },
                {
                  path: "/:clubid/manage/marketing/social",
                  element: <SocialPage />,
                  permission: "page_marketing_social",
                },
                {
                  path: "/:clubid/manage/marketing/posts",
                  element: <AllPostsPage />,
                  permission: "page_marketing_posts",
                },
                {
                  path: "/:clubid/manage/marketing/posts/new",
                  element: <NewPostPage />,
                  permission: "page_marketing_posts",
                },
                {
                  path: "/:clubid/manage/marketing/posts/edit/:postId",
                  element: <EditPostPage />,
                  permission: "page_marketing_posts",
                },
                {
                  path: "/:clubid/manage/marketing/posts/categories",
                  element: <CategoriesPage />,
                  permission: "page_marketing_posts_categories",
                },
                // ============================================
                // MARKETING ANALYTICS
                // ============================================
                {
                  path: "/:clubid/manage/marketing/analytics",
                  element: <MarketingDashboard />,
                  permission: "page_marketing_analytics",
                },
                {
                  path: "/:clubid/manage/marketing/discount-codes",
                  element: <DiscountCodesPage />,
                  // The same permission the discount code API checks
                  // (manage_discount_codes). The page asked for a different
                  // one, so a role could use the API but not open the page.
                  permission: "manage_discount_codes",
                },
                {
                  path: "/:clubid/manage/marketing/campaigns",
                  element: (
                    <ModuleGate moduleKey="marketing_communications">
                      <MarketingCampaignsPage />
                    </ModuleGate>
                  ),
                  permission: "page_marketing_campaigns",
                },
                {
                  path: "/:clubid/manage/design-lab",
                  element: <DesignLabPage />,
                  permission: "page_design_lab",
                },
                {
                  path: "/:clubid/manage/design-lab/:id",
                  element: <DesignEditorPage />,
                  permission: "page_design_lab",
                },
                {
                  path: "/:clubid/manage/marketing/campaigns-legacy",
                  element: (
                    <ModuleGate moduleKey="marketing_communications">
                      <CampaignsPage />
                    </ModuleGate>
                  ),
                  permission: "page_marketing_campaigns",
                },

                // ============================================
                // CRM
                // ============================================
                {
                  path: "/:clubid/manage/crm/leads",
                  permission: "page_crm_leads",
                  element: <LeadsPipelinePage token={token || ""} />,
                },
                {
                  // Read-only review list, reached from the Lead Pipeline.
                  path: "/:clubid/manage/crm/leads/converted-no-class",
                  permission: "page_crm_leads",
                  element: <ConvertedLeadsReviewPage token={token || ""} />,
                },
                {
                  path: "/:clubid/manage/crm/funnel",
                  permission: "page_crm_funnel",
                  element: <LeadFunnelPage token={token || ""} />,
                },
                {
                  path: "/:clubid/manage/crm/inbox",
                  permission: "page_crm_inbox",
                  element: (
                    <ComingSoonPage
                      title="Unified Inbox"
                      tagline="One place for every conversation — calls, texts, emails, and DMs — across all your channels. Reply without bouncing between apps."
                      bullets={[
                        {
                          title: "All channels in one feed",
                          description:
                            "SMS from Twilio, calls (with transcripts when available), staff↔member emails, and Facebook/Instagram DMs all stream into the same inbox.",
                        },
                        {
                          title: "Threaded by contact",
                          description:
                            "See the full back-and-forth with a member regardless of how they messaged you. Search by phone, email, or member name.",
                        },
                        {
                          title: "Reply from anywhere",
                          description:
                            "Hit reply and the response goes out on the channel they reached you on — no copy-paste, no app-switching.",
                        },
                        {
                          title: "Auto-replies + templates",
                          description:
                            "Club-configurable auto-replies for after-hours and missed calls, plus a library of reusable templates for common responses.",
                        },
                        {
                          title: "Read / unread / replied tracking",
                          description:
                            "Know what's been handled. Filter to only unread or only un-replied conversations to clear the queue fast.",
                        },
                      ]}
                      footnote="The backend for all of this is built — the UI is the last piece. Twilio account verification is the gating step for SMS-side features."
                    />
                  ),
                },
                {
                  path: "/:clubid/manage/crm/reviews",
                  element: <ReviewFunnelPage mode="review-funnel" />,
                  permission: "page_crm_reviews",
                },
                {
                  path: "/:clubid/manage/marketing/surveys",
                  element: (
                    <ModuleGate moduleKey="surveys_and_forms">
                      <ReviewFunnelPage mode="surveys" />
                    </ModuleGate>
                  ),
                  permission: "page_marketing_surveys",
                },

                // ============================================
                // BILLING
                // ============================================
                {
                  path: "/:clubid/manage/billing/invoice/new",
                  element: <CreateManualInvoice />,
                  permission: "generate_invoice_page",
                },
                {
                  path: "/:clubid/manage/billing/manual",
                  element: <CreateManualInvoice />,
                  permission: "generate_invoice_page",
                },
                {
                  path: "/:clubid/manage/billing/invoices/unpaid",
                  element: <ManageUnpaidInvoice />,
                  permission: "page_billing_invoices_unpaid",
                },
                {
                  path: "/:clubid/manage/billing/generate/:membershipId",
                  element: <GenerateManualInvoicePage />,
                  permission: "page_billing_generate",
                },
                {
                  path: "/:clubid/manage/billing/edit/:membershipId",
                  element: <EditDraftInvoicePage />,
                  permission: "page_billing_edit",
                },
                {
                  // Gated on manage_invoices to match the API
                  // (/api/competition-fees/{preview,generate}) — anyone who
                  // can run the batch can open the page, and nobody else.
                  path: "/:clubid/manage/billing/competition-fees",
                  element: <CompetitionFeeBatchPage />,
                  permission: "manage_invoices",
                },

                // ============================================
                // REPORTING
                // ============================================
                {
                  // Landing page for all reports — the sidebar's single
                  // "Reports" entry points here (2026-06 nav refresh).
                  path: "/:clubid/manage/reports",
                  element: <ReportsLandingPage />,
                  permission: "page_reports",
                },
                {
                  // Full "What's New" changelog (the modal's "Full
                  // changelog" link). Staff + admin.
                  path: "/:clubid/manage/whats-new",
                  element: <WhatsNewPage />,
                },
                {
                  path: "/:clubid/manage/reports/birthdays",
                  permission: "page_reports_birthdays",
                  element: <AthleteBirthdays />,
                },
                {
                  path: "/:clubid/manage/reports/ai-insights",
                  element: <AIInsightsPage />,
                  permission: "page_reports_ai_insights",
                },
                {
                  path: "/:clubid/manage/reports/balances",
                  element: <NegativeBalances />,
                  permission: "view_membership_invoices",
                  adminOnly: true,
                },
                {
                  path: "/:clubid/manage/reports/enrollments",
                  element: <AdminEnrollmentReport />,
                  permission: "page_reports_enrollments",
                },
                {
                  path: "/:clubid/manage/reports/members",
                  element: <MembershipsListPage />,
                  permission: "page_reports_members",
                },
                {
                  path: "/:clubid/manage/reports/revenue",
                  element: <RevenueReport />,
                  permission: "page_reports_revenue",
                },
                {
                  path: "/:clubid/manage/reports/revenue-projections",
                  element: <RevenueProjectionsReport />,
                  permission: "page_reports_revenue_projections",
                },
                {
                  path: "/:clubid/manage/reports/churn",
                  element: <ChurnRetentionReport />,
                  permission: "page_reports_churn",
                },
                {
                  path: "/:clubid/manage/reports/aging",
                  element: <AgingReport />,
                  permission: "page_reports_aging",
                },
                {
                  path: "/:clubid/manage/reports/incident-reports",
                  element: <IncidentReportsReport />,
                  permission: "page_reports_incident_reports",
                },
                {
                  path: "/:clubid/manage/reports/program-profitability",
                  element: <ProgramProfitabilityReport />,
                  permission: "page_reports_program_profitability",
                },
                {
                  path: "/:clubid/manage/reports/attendance-trends",
                  element: <AttendanceTrendsReport />,
                  permission: "page_reports_attendance_trends",
                },
                {
                  path: "/:clubid/manage/reports/registration-funnel",
                  element: <RegistrationFunnelReport />,
                  permission: "page_reports_registration_funnel",
                },
                {
                  path: "/:clubid/manage/reports/camp-event-funnel",
                  element: <CampEventFunnelReport />,
                  permission: "page_reports_camp_event_funnel",
                },
                {
                  path: "/:clubid/manage/reports/leads-by-source",
                  element: <LeadsBySourceReport />,
                  permission: "page_reports_leads_by_source",
                },
                {
                  path: "/:clubid/manage/reports/staff-activity",
                  element: <StaffActivityReport />,
                  permission: "page_reports_staff_activity",
                },
                {
                  path: "/:clubid/manage/reports/web-analytics",
                  element: <WebAnalyticsReport />,
                  permission: "page_reports_web_analytics",
                },
                {
                  path: "/:clubid/manage/reports/new-members",
                  element: <NewMemberAcquisitionReport />,
                  permission: "page_reports_new_members",
                },
                {
                  path: "/:clubid/manage/reports/seasonal-comparison",
                  element: <SeasonalComparisonReport />,
                  permission: "page_reports_seasonal_comparison",
                },
                {
                  path: "/:clubid/manage/reports/annual-registration-fees",
                  element: <AnnualRegistrationFeeReport />,
                  permission: "page_reports_annual_registration_fees",
                },
                {
                  path: "/:clubid/manage/reports/billing-reconciliation",
                  element: <BillingReconciliationReport />,
                  permission: "page_reports_billing_reconciliation",
                },
                {
                  path: "/:clubid/manage/reports/billing-run",
                  element: <BillingRunReport />,
                  // Same gate as the API (view_financial_reports): the email and
                  // the tasks link here, so whoever can read the data can open it.
                  permission: "view_financial_reports",
                },
                {
                  path: "/:clubid/manage/reports/email-verification",
                  element: <UnverifiedEmailsReportPage />,
                  permission: "page_reports_email_verification",
                },
                {
                  path: "/:clubid/manage/reports/top-customers",
                  element: <TopCustomersBySpendReport />,
                  permission: "page_reports_top_customers",
                },
                {
                  path: "/:clubid/manage/reports/payment-methods-breakdown",
                  element: <PaymentMethodsBreakdownReport />,
                  permission: "page_reports_payment_methods",
                },
                {
                  path: "/:clubid/manage/reports/stripe-payout-reconciliation",
                  element: <StripePayoutReconciliationReport />,
                  permission: "page_reports_stripe_payouts",
                },
                {
                  path: "/:clubid/manage/reports/year-end-statements",
                  element: <YearEndStatementsReport />,
                  permission: "page_reports_year_end_statements",
                },
                {
                  path: "/:clubid/manage/reports/membership-tenure",
                  element: <MembershipTenureReport />,
                  permission: "page_reports_membership_tenure",
                },
                {
                  path: "/:clubid/manage/reports/membership-demographics",
                  element: <MembershipDemographicsReport />,
                  permission: "page_reports_membership_demographics",
                },
                {
                  path: "/:clubid/manage/reports/athlete-demographics",
                  element: <AthleteDemographicsReport />,
                  permission: "page_reports_athlete_demographics",
                },
                {
                  path: "/:clubid/manage/reports/churn-risk",
                  element: <ChurnRiskReport />,
                  permission: "page_reports_churn_risk",
                },

                // ============================================
                // STAFF MANAGEMENT (Admin Only)
                // ============================================
                {
                  path: "/:clubid/manage/staff",
                  element: <ManageUsersPage />,
                  permission: "page_staff",
                },
                {
                  path: "/:clubid/manage/staff/add",
                  element: <AddStaffPage />,
                  permission: "page_staff",
                },
                {
                  path: "/:clubid/manage/staff/training",
                  element: <StaffTrainingPage />,
                  permission: "page_staff_training",
                },
                {
                  path: "/:clubid/manage/roles",
                  element: <RolePermissionsManager />,
                  permission: "page_roles",
                },

                // ============================================
                // SETTINGS (Admin Only)
                // ============================================
                {
                  path: "/:clubid/manage/settings",
                  element: <ManageSettingsPage />,
                  permission: "page_settings",
                },
                {
                  // Searchable index of every settings page — the
                  // sidebar's single "Settings" entry points here
                  // (2026-06 nav refresh). /manage/settings stays the
                  // General settings page.
                  path: "/:clubid/manage/settings/all",
                  element: <SettingsLandingPage />,
                  permission: "page_settings_all",
                },
                {
                  // Read-only "Your Plan" — Club Central subscription,
                  // add-ons, AI plan, included/available features.
                  path: "/:clubid/manage/settings/subscription",
                  element: <SubscriptionPage />,
                  permission: "page_settings_subscription",
                },
                {
                  path: "/:clubid/manage/settings/automation",
                  element: <AutomationSettingsPage />,
                  permission: "page_settings_automation",
                },
                {
                  path: "/:clubid/manage/settings/inactivity-automation",
                  element: <InactivitySettingsPage />,
                  permission: "page_settings_inactivity_automation",
                },
                {
                  // The club's own domain (phase 2 custom domains, PM
                  // 01fda321). The backend gates it to owners and admins
                  // (manage_tenant_settings); the page itself sits with the
                  // General settings page.
                  path: "/:clubid/manage/settings/domain",
                  element: <CustomDomainPage />,
                  permission: "page_settings",
                },
                {
                  path: "/:clubid/manage/settings/stripe-killswitch",
                  element: <StripeKillswitchPage />,
                  permission: "manage_stripe_killswitch",
                },
                {
                  path: "/:clubid/manage/settings/fees",
                  element: <FeeSettingsPage />,
                  permission: "page_settings_fees",
                },
                {
                  path: "/:clubid/manage/settings/email",
                  permission: "page_settings_email",
                  element: <EmailSettingsPage />,
                },
                {
                  path: "/:clubid/manage/settings/email-verification",
                  element: <EmailVerificationPage />,
                  permission: "verify_emails",
                  adminOnly: true,
                },
                {
                  path: "/:clubid/manage/settings/pricing",
                  element: <ProgramPricingSettings />,
                  permission: "page_settings_pricing",
                },
                {
                  path: "/:clubid/manage/settings/notifications",
                  element: <ManageNotificationPage />,
                  permission: "page_settings_notifications",
                },
                {
                  path: "/:clubid/manage/settings/email-templates",
                  element: <EmailTemplatesPage />,
                  permission: "page_settings_email_templates",
                },
                {
                  path: "/:clubid/manage/settings/store",
                  element: <StoreSettingsPage />,
                  permission: "page_settings_store",
                },
                {
                  path: "/:clubid/manage/settings/store/gp",
                  element: <GPSettingsPanel />,
                  permission: "page_settings_store",
                },
                {
                  path: "/:clubid/manage/settings/pos",
                  element: <PosSettingsPage />,
                  permission: "page_settings_pos",
                },
                {
                  path: "/:clubid/manage/settings/sms-diagnostics",
                  element: <SmsDiagnosticsPage />,
                  permission: "page_settings_sms_diagnostics",
                },
                {
                  path: "/:clubid/manage/settings/commissions",
                  element: <CommissionDashboard />,
                  permission: "page_settings_commissions",
                },
                {
                  path: "/:clubid/manage/settings/waivers",
                  element: <WaiversPage />,
                  permission: "page_settings_waivers",
                },
                {
                  path: "/:clubid/manage/settings/waivers/new",
                  element: <WaiverBuilderPage />,
                  permission: "page_settings_waivers",
                },
                {
                  path: "/:clubid/manage/settings/waivers/edit/:id",
                  element: <WaiverBuilderPage />,
                  permission: "page_settings_waivers",
                },
                {
                  path: "/:clubid/manage/settings/waivers/send/:id",
                  element: <WaiverSendPage />,
                  permission: "page_settings_waivers",
                },
                {
                  path: "/:clubid/manage/settings/waivers/missing",
                  element: <WaiversMissingPage />,
                  permission: "page_settings_waivers",
                },
                {
                  path: "/:clubid/manage/settings/waivers/missing-medical",
                  element: <MedicalInfoMissingPage />,
                  permission: "page_settings_waivers",
                },
                {
                  path: "/:clubid/manage/settings/action-types",
                  element: <ActionTypeSettingsPage />,
                  permission: "page_settings_action_types",
                },
                {
                  path: "/:clubid/manage/settings/support",
                  element: <SupportSettingsPage />,
                  permission: "page_settings_support",
                },
                {
                  path: "/:clubid/manage/settings/kiosk",
                  element: <KioskSettingsPage />,
                  permission: "page_settings_kiosk",
                },
                {
                  path: "/:clubid/manage/settings/integrations",
                  element: <IntegrationsSettingsPage />,
                  permission: "page_settings_integrations",
                },

                // ============================================
                // AI RECEPTIONIST (Admin Only — gated by ai_phone_receptionist module)
                // ============================================
                {
                  path: "/:clubid/manage/ai-receptionist",
                  element: (
                    <ModuleGate moduleKey="ai_phone_receptionist">
                      <AIReceptionistPage />
                    </ModuleGate>
                  ),
                  permission: "page_ai_receptionist",
                },
                {
                  path: "/:clubid/manage/ai-receptionist/knowledge",
                  element: (
                    <ModuleGate moduleKey="ai_phone_receptionist">
                      <KnowledgeManager />
                    </ModuleGate>
                  ),
                  permission: "page_ai_receptionist_knowledge",
                },
                {
                  path: "/:clubid/manage/ai-receptionist/conversations",
                  element: (
                    <ModuleGate moduleKey="ai_phone_receptionist">
                      <AIConversationsPage />
                    </ModuleGate>
                  ),
                  permission: "page_ai_receptionist_conversations",
                },
                {
                  path: "/:clubid/manage/ai-receptionist/analytics",
                  element: (
                    <ModuleGate moduleKey="ai_phone_receptionist">
                      <AIAnalyticsPage />
                    </ModuleGate>
                  ),
                  permission: "page_ai_receptionist_analytics",
                },
                {
                  path: "/:clubid/manage/ai-receptionist/call-routing",
                  element: (
                    <ModuleGate moduleKey="ai_phone_receptionist">
                      <CallRoutingSettingsPage />
                    </ModuleGate>
                  ),
                  permission: "manage_call_routing",
                },
                // Call history + voicemails. The "new call message"
                // notification links here as /<club>/manage/calls?message=<id>.
                {
                  path: "/:clubid/manage/calls",
                  element: (
                    <ModuleGate moduleKey="ai_phone_receptionist">
                      <CallHistoryPage />
                    </ModuleGate>
                  ),
                  permission: "view_call_history",
                },

                // ============================================
                // STORE / MERCHANDISE (Admin Only)
                // ============================================
                {
                  path: "/:clubid/manage/store/merchandise",
                  element: <MerchandisePage />,
                  permission: "page_store_merchandise",
                },
                {
                  path: "/:clubid/manage/store/equipment",
                  element: <EquipmentPage />,
                  permission: "page_store_equipment",
                },
                {
                  path: "/:clubid/manage/store/music",
                  element: <MusicAdminPage />,
                  permission: "page_store_music",
                },
                {
                  path: "/:clubid/manage/store/pos",
                  element: <PosCheckoutPage />,
                  permission: "page_store_pos",
                },
                {
                  path: "/:clubid/manage/store/orders",
                  element: <AdminOrdersPage />,
                  permission: "page_store_orders",
                },

                // ============================================
                // TRAINING / LMS
                // ============================================
                {
                  path: "/:clubid/manage/training/athletes",
                  element: <AthleteTrainingPage />,
                  permission: "page_training_athletes",
                },
                {
                  path: "/:clubid/manage/lms",
                  element: <LMSOverviewPage />,
                  permission: "page_lms",
                },
                {
                  path: "/:clubid/manage/lms/enrollments",
                  element: <EnrollmentsPage />,
                  permission: "page_lms_enrollments",
                },
                {
                  path: "/:clubid/manage/lms/awards",
                  element: <AwardsPage />,
                  permission: "page_lms_awards",
                },
                {
                  path: "/:clubid/manage/lms/courses/:courseId",
                  element: <CourseDetailPage />,
                  permission: "page_lms",
                },
                {
                  path: "/:clubid/manage/lms/courses/:courseId/learn",
                  element: <CoursePlayerPage />,
                  permission: "page_lms",
                },
                {
                  path: "/:clubid/manage/lms/courses/:courseId/builder",
                  element: <CourseBuilderPage />,
                  permission: "page_lms",
                },
                {
                  path: "/:clubid/manage/activity",
                  element: <ActivityLogPage />,
                  permission: "page_activity",
                },

                // ============================================
                // CAMPS (Admin Only)
                // ============================================
                {
                  path: "/:clubid/manage/camps",
                  element: <ManageCampsPage />,
                  permission: "page_camps",
                },
                {
                  path: "/:clubid/manage/camps/create",
                  element: <CreateCampPage />,
                  permission: "page_camps",
                },
                {
                  path: "/:clubid/manage/camps/:campId/edit",
                  element: <EditCampPage />,
                  permission: "page_camps",
                },
                {
                  path: "/:clubid/manage/camps/:campId/attendance",
                  element: <CampAttendancePage />,
                  permission: "page_camps",
                },

                // ============================================
                // CLINICS (Admin Only)
                // ============================================
                {
                  path: "/:clubid/manage/clinics",
                  element: <ManageClinicsPage />,
                  permission: "page_clinics",
                },
                {
                  path: "/:clubid/manage/clinics/create",
                  element: <CreateClinicPage />,
                  permission: "page_clinics",
                },
                {
                  path: "/:clubid/manage/clinics/:clinicId/edit",
                  element: <EditClinicPage />,
                  permission: "page_clinics",
                },
                {
                  path: "/:clubid/manage/clinics/:clinicId/attendance",
                  element: <ClinicAttendancePage />,
                  permission: "page_clinics",
                },

                // ============================================
                // PRIVATE SESSIONS (Admin Only)
                // ============================================
                {
                  path: "/:clubid/manage/private-sessions",
                  element: <ManagePrivateSessionsPage />,
                  permission: "page_private_sessions",
                },
                {
                  path: "/:clubid/manage/settings/private-sessions",
                  element: <PrivateSessionSettingsSection />,
                  permission: "page_settings_private_sessions",
                },

                // ============================================
                // HR MODULE (Admin Only)
                // ============================================
                {
                  path: "/:clubid/manage/hr/employees",
                  element: <ManageEmployeesPage />,
                  permission: "page_hr_employees",
                },
                {
                  path: "/:clubid/manage/hr/employees/create",
                  element: <EmployeeProfilePage />,
                  permission: "page_hr_employees",
                },
                {
                  path: "/:clubid/manage/hr/employees/:id",
                  element: <EmployeeProfilePage />,
                  permission: "page_hr_employees",
                },
                {
                  path: "/:clubid/manage/hr/time-tracking",
                  element: <TimeTrackingPage />,
                  permission: "page_hr_time_tracking",
                },
                {
                  path: "/:clubid/manage/hr/time-tracking/entries",
                  element: <TimeEntriesTable />,
                  permission: "page_hr_time_tracking",
                },
                {
                  path: "/:clubid/manage/hr/time-tracking/timesheets",
                  element: <TimesheetsPage />,
                  permission: "page_hr_time_tracking",
                },
                {
                  path: "/:clubid/manage/hr/time-tracking/timesheets/:id",
                  element: <TimesheetDetailPage />,
                  permission: "page_hr_time_tracking",
                },
                {
                  path: "/:clubid/manage/hr/onboarding",
                  element: <OnboardingDashboard />,
                  permission: "page_hr_onboarding",
                },
                {
                  path: "/:clubid/manage/hr/onboarding/templates",
                  element: <OnboardingTemplatesPage />,
                  permission: "page_hr_onboarding",
                },
                {
                  path: "/:clubid/manage/hr/onboarding/templates/create",
                  element: <TemplateBuilderPage />,
                  permission: "page_hr_onboarding",
                },
                {
                  path: "/:clubid/manage/hr/onboarding/templates/:id",
                  element: <TemplateBuilderPage />,
                  permission: "page_hr_onboarding",
                },
                {
                  path: "/:clubid/manage/hr/onboarding/workflows/:id",
                  element: <WorkflowDetailPage />,
                  permission: "page_hr_onboarding",
                },
                {
                  path: "/:clubid/manage/hr/documents",
                  element: <HRDocumentsPage />,
                  permission: "page_hr_documents",
                },
                {
                  path: "/:clubid/manage/hr/documents/create",
                  element: <DocumentEditorPage />,
                  permission: "page_hr_documents",
                },
                {
                  path: "/:clubid/manage/hr/documents/:id",
                  element: <DocumentViewPage />,
                  permission: "page_hr_documents",
                },
                {
                  path: "/:clubid/manage/hr/documents/:id/edit",
                  element: <DocumentEditorPage />,
                  permission: "page_hr_documents",
                },
                {
                  path: "/:clubid/manage/hr/payroll",
                  element: <PayrollComingSoon />,
                  permission: "page_hr_payroll",
                },

                // ============================================
                // EVENTS (Admin Only)
                // ============================================
                {
                  path: "/:clubid/manage/events",
                  element: <ManageEventsPage />,
                  permission: "page_events",
                },
                {
                  path: "/:clubid/manage/events/create",
                  element: <CreateEventPage />,
                  permission: "page_events",
                },
                {
                  path: "/:clubid/manage/events/:id",
                  element: <EventDetailPage />,
                  permission: "page_events",
                },
                {
                  path: "/:clubid/manage/events/:id/edit",
                  element: <EditEventPage />,
                  permission: "page_events",
                },
                {
                  path: "/:clubid/manage/events/:eventId/attendance",
                  element: <EventAttendancePage />,
                  permission: "page_events",
                },
                {
                  path: "/:clubid/manage/events/:eventId/registrations",
                  element: <EventRegistrationsPage />,
                  permission: "page_events",
                },
                {
                  // Per-time-slot roster: who signed up and what they bought (PM 4e4384e4)
                  path: "/:clubid/manage/events/:eventId/roster",
                  element: <EventSlotRosterPage />,
                  permission: "page_events",
                },
                {
                  path: "/:clubid/manage/events/:eventId/competition-setup",
                  element: <CompetitionSetupWizard />,
                  permission: "page_events",
                },
                {
                  path: "/:clubid/manage/competitions/:competitionId",
                  element: <CompetitionDetailPage />,
                  permission: "page_events",
                },
                {
                  path: "/:clubid/competitions/live/:token",
                  element: <PublicCompetitionPage />,
                  // Public page — no adminOnly. Token-only auth.
                },
                {
                  path: "/:clubid/manage/competitions/:competitionId/announcement",
                  element: <AwardsAnnouncementScreen />,
                  permission: "page_events",
                },
                {
                  path: "/:clubid/manage/events/templates",
                  element: <EventTemplatesPage />,
                  permission: "page_events_templates",
                },
                {
                  path: "/:clubid/manage/events/analytics",
                  element: <EventAnalyticsPage />,
                  permission: "page_events_analytics",
                },
                {
                  path: "/:clubid/manage/events/birthday-parties",
                  element: <BirthdayPartiesPage />,
                  permission: "page_events_birthday_parties",
                },

                // ============================================
                // SUPPORT
                // ============================================
                {
                  path: "/:clubid/manage/support",
                  permission: "page_support",
                  element: <SupportPage />,
                },
                {
                  path: "/:clubid/manage/knowledgebase",
                  permission: "page_knowledgebase",
                  element: <KnowledgebasePage />,
                },
              ]
                .filter(({ adminOnly }) => !adminOnly || isAdmin)
                .map(({ path, element, permission }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin", "staff"]}
                        requiredPermission={permission}
                      >
                        <SideNavbarLayout>{element}</SideNavbarLayout>
                      </ProtectedRoute>
                    }
                  />
                ))}
            </>
          )}

          {/* ============================================
              LEGACY REDIRECTS (Backwards Compatibility)
              ============================================
              Redirects from old /admin/* and /staff/* paths to new /manage/* paths
          */}
          {[
            // Admin legacy redirects
            { from: "/:clubid/admin/view-memberships", to: "/:clubid/manage/memberships" },
            { from: "/:clubid/admin/add-member", to: "/:clubid/manage/memberships/add" },
            { from: "/:clubid/admin/view-invoice", to: "/:clubid/manage/memberships/invoices" },
            { from: "/:clubid/admin/reassign", to: "/:clubid/manage/memberships/reassign" },
            { from: "/:clubid/admin/manage-athletes", to: "/:clubid/manage/athletes" },
            { from: "/:clubid/admin/athlete/add", to: "/:clubid/manage/athletes/add" },
            { from: "/:clubid/admin/athlete/edit/:athleteId", to: "/:clubid/manage/athletes/edit/:athleteId" },
            { from: "/:clubid/admin/teams", to: "/:clubid/manage/teams" },
            { from: "/:clubid/admin/levels", to: "/:clubid/manage/levels" },
            { from: "/:clubid/admin/mark-attendance", to: "/:clubid/manage/attendance" },
            { from: "/:clubid/admin/recent-absence", to: "/:clubid/manage/attendance/absences" },
            { from: "/:clubid/admin/manage-programs", to: "/:clubid/manage/programs" },
            { from: "/:clubid/admin/create-programs", to: "/:clubid/manage/programs/create" },
            { from: "/:clubid/admin/marketing/broadcasts", to: "/:clubid/manage/marketing/broadcasts" },
            { from: "/:clubid/admin/marketing/new-broadcast", to: "/:clubid/manage/marketing/broadcasts/new" },
            { from: "/:clubid/admin/group-email", to: "/:clubid/manage/marketing/broadcasts/new" },
            // Saved segments folded into contact lists (2026-08-10) — one noun
            // for "a named group of people". Bookmarks land on the lists panel.
            { from: "/:clubid/manage/crm/segments", to: "/:clubid/manage/marketing/contacts" },
            { from: "/:clubid/admin/marketing/contacts", to: "/:clubid/manage/marketing/contacts" },
            { from: "/:clubid/admin/marketing/social", to: "/:clubid/manage/marketing/social" },
            { from: "/:clubid/admin/posts", to: "/:clubid/manage/marketing/posts" },
            { from: "/:clubid/admin/posts/new", to: "/:clubid/manage/marketing/posts/new" },
            { from: "/:clubid/admin/posts/edit/:postId", to: "/:clubid/manage/marketing/posts/edit/:postId" },
            { from: "/:clubid/admin/posts/categories", to: "/:clubid/manage/marketing/posts/categories" },
            { from: "/:clubid/admin/generate-invoice", to: "/:clubid/manage/billing/invoice/new" },
            // Legacy /admin/invoices used to target the antiquated
            // "Invoice & Payment Management" report. Redirect now points
            // at Reports > Revenue, which carries the same audit usage
            // with a healthier UI.
            { from: "/:clubid/admin/invoices", to: "/:clubid/manage/reports/revenue" },
            { from: "/:clubid/admin/unpaid-invoices", to: "/:clubid/manage/billing/invoices/unpaid" },
            { from: "/:clubid/admin/generate-manual-invoice/:membershipId", to: "/:clubid/manage/billing/generate/:membershipId" },
            { from: "/:clubid/admin/edit-invoice/:membershipId", to: "/:clubid/manage/billing/edit/:membershipId" },
            // Never a real route — EditAthlete's back button and the monthly-dues
            // vacation card both generated it, and both 404'd (2026-09-01).
            { from: "/:clubid/admin/edit-membership/:membershipId", to: "/:clubid/manage/memberships/:membershipId" },
            // Never a real route: "task assigned" notifications for a lead and
            // the dashboard Leads widget both linked to it and landed on the
            // tenant home page (found 2026-10-08). A lead is a membership row.
            { from: "/:clubid/manage/crm/leads/:membershipId", to: "/:clubid/manage/memberships/:membershipId" },
            { from: "/:clubid/admin/reports/birthdays", to: "/:clubid/manage/reports/birthdays" },
            { from: "/:clubid/admin/reports/enrollments", to: "/:clubid/manage/reports/enrollments" },
            { from: "/:clubid/admin/negative-balances", to: "/:clubid/manage/reports/balances" },
            { from: "/:clubid/admin/members", to: "/:clubid/manage/reports/members" },
            { from: "/:clubid/admin/manage-users", to: "/:clubid/manage/staff" },
            { from: "/:clubid/admin/add-staff", to: "/:clubid/manage/staff/add" },
            { from: "/:clubid/admin/staff-training", to: "/:clubid/manage/staff/training" },
            { from: "/:clubid/admin/manage-settings", to: "/:clubid/manage/settings" },
            { from: "/:clubid/admin/manage-fee", to: "/:clubid/manage/settings/fees" },
            { from: "/:clubid/admin/manage-email", to: "/:clubid/manage/settings/email" },
            { from: "/:clubid/admin/pricing-settings", to: "/:clubid/manage/settings/pricing" },
            { from: "/:clubid/admin/email-templates", to: "/:clubid/manage/settings/email-templates" },
            { from: "/:clubid/admin/store-settings", to: "/:clubid/manage/settings/store" },
            { from: "/:clubid/admin/gp-settings", to: "/:clubid/manage/settings/store/gp" },
            { from: "/:clubid/admin/commissions", to: "/:clubid/manage/settings/commissions" },
            { from: "/:clubid/admin/waivers", to: "/:clubid/manage/settings/waivers" },
            { from: "/:clubid/admin/waivers/new", to: "/:clubid/manage/settings/waivers/new" },
            { from: "/:clubid/admin/waivers/edit/:id", to: "/:clubid/manage/settings/waivers/edit/:id" },
            { from: "/:clubid/admin/action-types", to: "/:clubid/manage/settings/action-types" },
            { from: "/:clubid/admin/support-settings", to: "/:clubid/manage/settings/support" },
            { from: "/:clubid/admin/kiosk-settings", to: "/:clubid/manage/settings/kiosk" },
            { from: "/:clubid/admin/merchandise", to: "/:clubid/manage/store/merchandise" },
            { from: "/:clubid/admin/equipment", to: "/:clubid/manage/store/equipment" },
            { from: "/:clubid/admin/music", to: "/:clubid/manage/store/music" },
            { from: "/:clubid/admin/athlete-training", to: "/:clubid/manage/training/athletes" },
            { from: "/:clubid/admin/manage-camps", to: "/:clubid/manage/camps" },
            { from: "/:clubid/admin/create-camp", to: "/:clubid/manage/camps/create" },
            { from: "/:clubid/admin/camps/:campId/edit", to: "/:clubid/manage/camps/:campId/edit" },
            { from: "/:clubid/admin/camps/:campId/attendance", to: "/:clubid/manage/camps/:campId/attendance" },
            { from: "/:clubid/admin/manage-events", to: "/:clubid/manage/events" },
            { from: "/:clubid/admin/create-event", to: "/:clubid/manage/events/create" },
            { from: "/:clubid/admin/events/:id", to: "/:clubid/manage/events/:id" },
            { from: "/:clubid/admin/events/:id/edit", to: "/:clubid/manage/events/:id/edit" },
            { from: "/:clubid/admin/events/:eventId/attendance", to: "/:clubid/manage/events/:eventId/attendance" },
            { from: "/:clubid/admin/events/templates", to: "/:clubid/manage/events/templates" },
            { from: "/:clubid/admin/events/analytics", to: "/:clubid/manage/events/analytics" },
            { from: "/:clubid/admin/support", to: "/:clubid/manage/support" },
            { from: "/:clubid/admin/knowledgebase", to: "/:clubid/manage/knowledgebase" },
            { from: "/:clubid/manage-roles", to: "/:clubid/manage/roles" },
            { from: "/:clubid/manage-notifications", to: "/:clubid/manage/settings/notifications" },

            // Staff legacy redirects
            { from: "/:clubid/staff/view-memberships", to: "/:clubid/manage/memberships" },
            { from: "/:clubid/staff/add-member", to: "/:clubid/manage/memberships/add" },
            { from: "/:clubid/staff/view-invoice", to: "/:clubid/manage/memberships/invoices" },
            { from: "/:clubid/staff/manage-athletes", to: "/:clubid/manage/athletes" },
            { from: "/:clubid/staff/athlete/add", to: "/:clubid/manage/athletes/add" },
            { from: "/:clubid/staff/athlete/edit/:athleteId", to: "/:clubid/manage/athletes/edit/:athleteId" },
            { from: "/:clubid/staff/edit-membership/:membershipId", to: "/:clubid/manage/memberships/:membershipId" },
            { from: "/:clubid/staff/teams", to: "/:clubid/manage/teams" },
            { from: "/:clubid/staff/levels", to: "/:clubid/manage/levels" },
            { from: "/:clubid/staff/mark-attendance", to: "/:clubid/manage/attendance" },
            { from: "/:clubid/staff/recent-absence", to: "/:clubid/manage/attendance/absences" },
            { from: "/:clubid/staff/marketing/broadcasts", to: "/:clubid/manage/marketing/broadcasts" },
            { from: "/:clubid/staff/marketing/new-broadcast", to: "/:clubid/manage/marketing/broadcasts/new" },
            { from: "/:clubid/staff/marketing/contacts", to: "/:clubid/manage/marketing/contacts" },
            { from: "/:clubid/staff/generate-invoice", to: "/:clubid/manage/billing/invoice/new" },
            { from: "/:clubid/staff/reports/birthdays", to: "/:clubid/manage/reports/birthdays" },
            { from: "/:clubid/staff/support", to: "/:clubid/manage/support" },
            { from: "/:clubid/staff/knowledgebase", to: "/:clubid/manage/knowledgebase" },
          ].map(({ from, to }) => (
            <Route
              key={from}
              path={from}
              element={<LegacyRedirect to={to} />}
            />
          ))}

          {isMember && (
            <>
              {[
                {
                  // NO requiredPermission: a family's own invoices. It carried
                  // the STAFF permission view_membership_invoices, which no
                  // family role has, so every parent got Access Denied. The
                  // server only returns invoices of an account the signed-in
                  // person is on (GET /invoices/member/:membershipId is
                  // ownership-gated).
                  path: "/:clubid/member/view-invoice",
                  element: <InvoicePaymentHistory />,
                },
                {
                  path: "/:clubid/member/athlete/add",
                  element: <AddAthlete />,
                },
                {
                  path: "/:clubid/member/view-billing",
                  element: <MemberBillingHistory />,
                },
                {
                  path: "/:clubid/member/update-card",
                  element: <UpdateCardForm />,
                },
                {
                  path: "/:clubid/member/manage-profile",
                  element: <ManageProfilePage />,
                },
                {
                  path: "/:clubid/member/facilities",
                  element: <MemberFacilityBookingPage />,
                },
                {
                  path: "/:clubid/member/facilities/bookings",
                  element: <MemberFacilityBookingsPage />,
                },
                {
                  path: "/:clubid/member/convert-trial",
                  element: <TrialConvert />,
                },
                {
                  // NO requiredPermission — these two are member-scoped pages
                  // for a parent's OWN athletes. They carried the STAFF
                  // permissions `manage_athletes_page` / `delete_athletes`
                  // (5cb3f210, 2025-09-19 role-routing pass), but member
                  // permissions in Redux come from the `member` ROLE's
                  // role_permissions map, which is empty on real tenants — so
                  // every parent got AccessDenied on their own athletes list
                  // and edit page (found 2026-07-28 chasing "parents can't add
                  // their uniform sizes"; the attire section only exists here).
                  // Member authorization is per-membership, not per-role, and
                  // is enforced server-side by getCallerContext (primary vs.
                  // secondary + edit_athletes / view_athlete_info) on every
                  // athlete read/write, including PATCH /athletes/:id/attire.
                  path: "/:clubid/member/manage-athlete",
                  element: <ManageAthletes />,
                },
                {
                  path: "/:clubid/member/athlete/edit/:athleteId",
                  element: <EditAthlete />,
                },
                {
                  path: "/:clubid/advance-payment",
                  element: <PayInAdvance />,
                },
                {
                  path: "/:clubid/member/contact",
                  element: <PrivateContactPage asPage />,
                },
                {
                  path: "/:clubid/dashboard",
                  element: <Dashboard />,
                },
                {
                  path: "/:clubid/member/feedback",
                  element: <FeedbackPage />,
                },
                {
                  path: "/:clubid/member/news",
                  element: <News />,
                },
                {
                  path: "/:clubid/member/help",
                  element: <FamilyHelpPage />,
                },
                {
                  path: "/:clubid/member/invoice/:invoiceId",
                  element: <MemberInvoice />,
                },
                {
                  path: "/:clubid/member/store",
                  element: (
                    <StorefrontRouteGuard>
                      <MemberStorePage />
                    </StorefrontRouteGuard>
                  ),
                },
                {
                  path: "/:clubid/member/cart",
                  element: (
                    <StorefrontRouteGuard>
                      <CartPage />
                    </StorefrontRouteGuard>
                  ),
                },
                {
                  path: "/:clubid/member/checkout",
                  element: (
                    <StorefrontRouteGuard>
                      <CheckoutPage />
                    </StorefrontRouteGuard>
                  ),
                },                {                  path: "/:clubid/member/music",                  element: (<StorefrontRouteGuard><MusicStorePage /></StorefrontRouteGuard>),                },                {                  path: "/:clubid/member/orders",                  element: (<StorefrontRouteGuard><OrderHistoryPage /></StorefrontRouteGuard>),                },                {                  path: "/:clubid/member/order-confirmation/:orderId",                  element: (<StorefrontRouteGuard><OrderConfirmationPage /></StorefrontRouteGuard>),
                },
                {
                  path: "/:clubid/member/events",
                  element: <MemberEventsPage />,
                },
                {
                  path: "/:clubid/member/calendar",
                  element: <CalendarPage />,
                },
                {
                  path: "/:clubid/member/my-events",
                  element: <MyEventsPage />,
                },
                {
                  path: "/:clubid/member/camps",
                  element: <MemberCampsPage />,
                },
                {
                  path: "/:clubid/member/my-camps",
                  element: <MemberMyCampsPage />,
                },
                {
                  path: "/:clubid/member/camp-calendar",
                  element: <CampCalendarPage />,
                },
                {
                  path: "/:clubid/member/clinics",
                  element: <MemberClinicsPage />,
                },
                {
                  path: "/:clubid/member/my-clinics",
                  element: <MemberMyClinicsPage />,
                },
                {
                  path: "/:clubid/member/clinic-calendar",
                  element: <ClinicCalendarPage />,
                },
              ].map(({ path, element, permission }: { path: string; element: React.ReactNode; permission?: string }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute
                      requiredPermission={permission}
                      allowedRoles={["member"]}
                    >
                      <SideNavbarLayout>{element}</SideNavbarLayout>
                    </ProtectedRoute>
                  }
                />
              ))}
            </>
          )}

          {/* Fallback route */}
          <Route
            path="*"
            element={<Navigate to={tenantId ? `/${tenantId}` : "/"} replace />}
          />
          </Routes>
            </Suspense>
            {!currentPath.includes('/kiosk') && !currentPath.includes('/embed/') && <FloatingChatGate />}
            {/* Staff idle sign-out (PM aa0f43a7): renders nothing unless the club set a limit for this staff session. */}
            {!currentPath.includes('/kiosk') && !currentPath.includes('/embed/') && <StaffIdleSignOut />}
            </CallCenterProvider>
            </ChatProvider>
            </NotificationProvider>
          </OfflineQueueProvider>
        </CartProvider>
        </EntitlementProvider>
        </PermissionProvider>
      </TenantProvider>
    </AuthProvider>
  );
}

// Error fallback component for Sentry Error Boundary
function ErrorFallback({ error, componentStack, resetError }: {
  error: unknown;
  componentStack: string | null;
  resetError: () => void;
}) {
  // Vaeth + -3K-issue (2026-06-05): users get stuck on a stale JS bundle
  // crash, click "Try Again" which resets the error boundary but re-runs
  // the same buggy render → loop forever. Most render crashes are render-
  // path bugs that have already been fixed; the user just needs to load
  // the new bundle. Make "Reload App" the primary action so they can
  // escape the loop. "Try Again" stays as a secondary for legitimate
  // cases (transient render error, e.g. a flaky network-driven state).
  //
  // window.location.reload(true) is deprecated but still respected by
  // browsers for a cache-busting reload. We call replace() with a cache-
  // busting query parameter for a more portable forced refresh.
  const handleHardReload = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("_r", String(Date.now()));
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
        <div className="text-red-500 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Something went wrong
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          We've been notified and are working to fix the issue. The app may
          have been updated since this tab was loaded — reloading usually
          fixes it.
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={handleHardReload}
            className="bg-[var(--cc-action)] hover:bg-[var(--cc-action-hover)] text-[var(--cc-action-text)] font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Reload App
          </button>
          <button
            onClick={resetError}
            className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, resetError }) => (
        <ErrorFallback error={error} componentStack={componentStack} resetError={resetError} />
      )}
      onError={(error, componentStack, eventId) => {
        console.error("Caught by Sentry Error Boundary:", error);
        console.error("Component Stack:", componentStack);
        console.error("Sentry Event ID:", eventId);
      }}
    >
      <HelmetProvider>
        <ThemeProvider>
          <LanguageProvider>
            <Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}>
              <ErrorToastProvider>
                <AppContent />
              </ErrorToastProvider>
            </Suspense>
          </LanguageProvider>
        </ThemeProvider>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
